import { Chain } from '@1delta/chain-registry'
import type { YieldFetcher } from '../../yields/types'
import type { CounterpartyTerms } from '../../terms/types'
import { susdeFetcher } from '../../yields/intrinsic/fetchers/ethena'
import { susdsFetcher } from '../../yields/intrinsic/fetchers/sky'
import { srusdFetcher } from '../../yields/intrinsic/fetchers/reservoir'
import { yusdFetcher } from '../../yields/intrinsic/fetchers/yieldfi'
import { angleFetcher } from '../../yields/intrinsic/fetchers/angle'
import { susdfFetcher } from '../../yields/intrinsic/fetchers/falcon'
import {
  savusdFetcher,
  savethFetcher,
  savbtcFetcher,
} from '../../yields/intrinsic/fetchers/avant'
import { infinifiFetcher } from '../../yields/intrinsic/fetchers/infinifi'
import { mapleFetcher } from '../../yields/intrinsic/fetchers/maple'
import { yoFetcher } from '../../yields/intrinsic/fetchers/yoProtocol'
import { wstusrFetcher } from '../../yields/intrinsic/fetchers/resolv'
import { hastraPrimeFetcher } from '../../yields/intrinsic/fetchers/hastra'
import {
  sparkSavingsFetcher,
  sparkVaultV2Key,
  SPARK_SAVINGS_USDG_KEY,
  SPARK_SUSDC_KEY,
} from '../../yields/intrinsic/fetchers/sparkSavings'
import { fraxSavingsFetcher } from '../../yields/intrinsic/fetchers/frax'
import { sreusdSavingsFetcher } from '../../yields/intrinsic/fetchers/resupply'
import {
  fxSaveFetcher,
  FXSAVE_KEY,
} from '../../yields/intrinsic/fetchers/fxProtocol'
import {
  parallelFetcher,
  SUSDP_KEY,
} from '../../yields/intrinsic/fetchers/parallel'
import { rwaFundsFetcher } from '../../yields/intrinsic/fetchers/rwaFunds'
import {
  sdolaFetcher,
  SDOLA_GROUP_KEY,
} from '../../yields/intrinsic/fetchers/inverse'
import {
  reProtocolFetcher,
  REUSD_KEY,
  REUSDE_KEY,
} from '../../yields/intrinsic/fetchers/reProtocol'
import {
  threeJaneFetcher,
  USD3_KEY,
  SUSD3_KEY,
} from '../../yields/intrinsic/fetchers/threeJane'
import { toriFetcher, STRUSD_KEY } from '../../yields/intrinsic/fetchers/tori'
import { snusdFetcher, SNUSD_KEY } from '../../yields/intrinsic/fetchers/neutrl'
import { brixFetcher, WITRY_KEY } from '../../yields/intrinsic/fetchers/brix'
import { capFetcher, STCUSD_KEY } from '../../yields/intrinsic/fetchers/cap'
import { wrenFetcher, WSTGBP_KEY } from '../../yields/intrinsic/fetchers/wren'
import { hyperbeatVaultsFetcher } from '../../yields/intrinsic/fetchers/hyperbeat'
import { apyxFetcher, APYUSD_KEY } from '../../yields/intrinsic/fetchers/apyx'
import {
  strataFetcher,
  SRUSDE_KEY,
  JRUSDE_KEY,
  SRNUSD_KEY,
  JRNUSD_KEY,
  SRMHYPER_KEY,
  JRMHYPER_KEY,
  SRMM1USD_KEY,
  JRMM1USD_KEY,
  SRUSDAT_KEY,
  JRUSDAT_KEY,
  SRPRIME_KEY,
  JRPRIME_KEY,
} from '../../yields/intrinsic/fetchers/strata'
import {
  scrvusdFetcher,
  SCRVUSD_KEY,
} from '../../yields/intrinsic/fetchers/curve'
import {
  saturnFetcher,
  SUSDAT_KEY,
} from '../../yields/intrinsic/fetchers/saturn'
import { sthusdFetcher, STHUSD_KEY } from '../../yields/intrinsic/fetchers/theo'
import {
  humaPstFetcher,
  HUMA_PST_KEY,
  HUMA_PST_FEED,
} from '../../yields/intrinsic/fetchers/huma'
import { usddFetcher, susddKey } from '../../yields/intrinsic/fetchers/usdd'
import {
  frankencoinSavingsFetcher,
  zchfSavingsKey,
  FRANKENCOIN_SAVINGS_MODULES,
  ZCHF_BRIDGED,
  ZCHF_ETHEREUM,
} from '../../yields/intrinsic/fetchers/frankencoin'
import {
  yieldBasisFetcher,
  YB_CBBTC_KEY,
  YB_TBTC_KEY,
  YB_WBTC_KEY,
  YB_WETH_KEY,
} from '../../yields/intrinsic/fetchers/yieldBasis'
import type { SavingsReaderKind } from './readers'
import { NATIVE_WNLP_ENTRIES } from './registryNative'
import { BITFI_ENTRIES } from './registryBitfi'
import { BITWAY_ENTRIES } from './registryBitway'
import { VESPER_ENTRIES } from './registryVesper'
import { VENUS_HUB_ENTRIES } from './registryVenusHub'
import type { SavingsWithdrawalMode } from './types'

/**
 * Everything the `venus-hub` reader needs that it cannot discover in a single
 * multicall round.
 *
 * A Hub's YieldGroups are enumerable on-chain (`registeredYieldGroups()`) and
 * its Core market is enumerable from the group (`resources()`), but both are
 * one hop too deep: a discovered address cannot be a call target in the same
 * round, and the savings orchestrator issues exactly one. So the roster is
 * pinned and `test/vaults/venusHub.live.test.ts` asserts it against the chain
 * — the same pin-and-test discipline the Aave e-mode count uses.
 *
 * An UNPINNED group added on-chain is simply not counted, which under-reports
 * liquidity and under-weights the rate. That is the safe direction for both,
 * which is what makes the pin acceptable at all.
 */
export interface VenusHubConfig {
  /** Every registered YieldGroup, in `registeredYieldGroups()` order. Their
   *  `maxWithdraw()` sums into the row's liquidity and their
   *  `totalAssets()`/`spotAPYBps()` into its rate. */
  yieldGroups: string[]
  /**
   * The Venus Core leg, for the emission read. Absent ⇒ no emission figure is
   * published for the row at all (rather than a comfortable `0`).
   *
   * Only the Core family has claim-based incentives that bypass the share
   * price: Fluid's rewards are already inside `AdapterFlux.spotAPYBps` and
   * reach depositors through the fToken's exchange price, and an FRV pays a
   * contractual coupon with no emission at all.
   */
  core?: {
    /** The Core YieldGroup — must be one of `yieldGroups`, matched by
     *  identity, and supplies both the emission weight and the
     *  `blocksPerYear()` annualiser the Core adapter itself uses. */
    yieldGroup: string
    /** The vToken that group supplies into. */
    vToken: string
    /** Comptroller holding `venusSupplySpeeds`. */
    comptroller: string
    /** `ResilientOracle` pricing both the reward token and the underlying. */
    oracle: string
    /** Emission token (XVS). */
    rewardToken: string
  }
}

/**
 * Static registry entry for a savings vault.
 *
 * Per-chain entries live in {@link SAVINGS_REGISTRY}. Each pins the
 * on-chain identity (address, decimals), brand metadata, the
 * withdrawal mechanism (drives UX), and a pointer to the existing
 * intrinsic-yield fetcher whose APR is surfaced as `supplyRate`.
 *
 * `reader` defaults to the generic ERC-4626 reader; entries whose
 * share token exposes a bespoke surface (Native's wNLP) set it and get
 * a case in [`readers/`](./readers/).
 */
export interface SavingsRegistryEntry {
  /** Read surface. Omit for plain ERC-4626 (the overwhelming majority). */
  reader?: SavingsReaderKind
  /** Share-token contract address, lowercased. */
  address: string
  /** Underlying ERC-20 address, lowercased. */
  underlying: string
  /** Share-token symbol — usually the on-chain `symbol()`. */
  symbol: string
  /**
   * What to call the PRODUCT when its symbol is not a name.
   *
   * Almost every row leaves this absent: the share token has a symbol of its
   * own (`sUSDe`, `wstETH`, `svZCHF`) and a listing that reads
   * "Ethena · sUSDe" says exactly what it is. The exception is a position
   * that is NOT a token — Frankencoin's savings module books a balance into
   * an internal account, so its honest `symbol` is the UNDERLYING's ticker
   * (see `frankencoinSavingsBase`). Composing a name from that ticker names
   * the row after the money it holds rather than after the product, which is
   * both uninformative and — since the module's own tokenised wrapper is a
   * separate row on the same chain — ambiguous.
   *
   * Set it only when the symbol is the underlying's; do not use it to
   * decorate a row that already has a share ticker.
   */
  label?: string
  /** Brand label (`Ethena`, `Sky`, `Maker`, …). */
  brand: string
  /** One-to-three-sentence user-facing explainer: what the underlying
   *  is, where the yield comes from, and the exit mechanics / trust
   *  caveat when load-bearing. Surfaced verbatim as
   *  `SavingsVault.description`. */
  description: string
  /** Share decimals. Most are 18; Maple syrup* are 6. */
  decimals: number
  /** Underlying ERC-20 decimals. Defaults to `decimals` when absent
   *  — every ERC-4626 vault except a handful keep share/underlying
   *  decimals aligned. yUSD breaks this (18-decimal shares over
   *  6-decimal USDC) so it must set this explicitly. */
  underlyingDecimals?: number
  /** True if the share token rebases. False for nearly every
   *  savings vault — the rebase lives on the underlying. */
  isRebasing: boolean
  /** Whether deposits are permissionless. */
  isMintable: boolean
  /** Mint entry — undefined ⇒ share token itself (ERC-4626
   *  `deposit/mint`). */
  mintContract?: string
  /**
   * The protocol's own mint/redeem is PERMISSIONED (KYC, allowlist) while the
   * share token itself transfers freely and has real secondary depth — so the
   * only route our users have, in BOTH directions, is a trade.
   *
   * Distinct from `isMintable: false`, which states the permission and stops
   * there: a merely-gated row has no entry at all and the earn surface renders
   * a disabled CTA. A `secondaryMarketOnly` row DOES have an entry — a swap,
   * priced by pool depth rather than by the vault, so both legs need a
   * slippage bound and neither may be built without one.
   *
   * NB the row's `exchangeRate` stays the NAV. The trade fills at the MARKET
   * price, which may sit at a discount or a premium to it — Re documents that
   * gap as the point of the trade ("buying below NAV"). Never present the two
   * as the same number.
   */
  secondaryMarketOnly?: boolean
  /**
   * Seconds a FRESH deposit earns nothing before the rate starts applying.
   *
   * NOT a withdrawal lock — `withdrawalCooldownSeconds` is how long the money
   * is stuck, and on the one entry that sets this the exit is instant. It is a
   * warm-up: Frankencoin's savings modules stamp a new account's tick counter
   * 3 days into the future (`INTEREST_DELAY`), so a shorter stay realises
   * EXACTLY zero, and a top-up re-weights the whole position's clock pro-rata
   * rather than only the new money.
   *
   * Surfaces as `termSheet.supply.rate.warmupSecs` and in the headline, which
   * would otherwise read "3.5 % · withdraw any time" and be misleading by
   * omission.
   */
  yieldWarmupSeconds?: number
  /**
   * `linear` when the accrual does NOT compound on its own.
   *
   * Absent ⇒ compounding, correct for any vault whose share price grows
   * continuously. Frankencoin books `Δticks × saved / 1e6 / 365 days` and only
   * compounds when someone calls `refresh`, so a per-second label would imply
   * an APY ~2 % relative above what it pays.
   */
  accrual?: 'linear' | 'compounding'
  /**
   * `false` when a deposit needs NO ERC-20 approval.
   *
   * Absent ⇒ true, which is right for every vault that pulls with
   * `transferFrom` against an ordinary token. Frankencoin's modules are
   * registered ZCHF minters and the token grants a registered minter an
   * implicit infinite allowance, so their deposit route emits no approval —
   * and a term sheet claiming one would contradict the envelope beside it.
   */
  needsDepositApproval?: boolean
  /**
   * How a USER's position is read. Absent ⇒ `balanceOf` on `address`.
   *
   * `savings-account` is for an address that is NOT a token: Frankencoin's
   * module answers `savings(address)`, and `balanceOf` REVERTS on it — which
   * the balance route swallowed into a zero, so a funded account rendered
   * empty.
   */
  balanceKind?: 'erc20' | 'savings-account'
  /** Withdrawal mechanism. */
  withdrawalMode: SavingsWithdrawalMode
  /** Fixed cooldown in seconds for `fixed-cooldown` mode. Pinned
   *  here; read on-chain if the precise governance-current value is
   *  needed. Also the pinned fallback for `fee-or-queued` entries whose
   *  reader re-reads the live queue window. */
  withdrawalCooldownSeconds?: number
  /** Contract a delayed redemption is requested from, when it is not the
   *  share token itself (Native's per-wNLP `WithdrawQueue`). */
  withdrawQueue?: string
  /** Contract holding the inventory an *instant* exit is drawn from,
   *  when the share token itself does not custody it (Native's per-chain
   *  `CreditVault`, Spark V1's PSM3 pocket). Read as
   *  `underlying.balanceOf(inventoryContract)` to get the real
   *  this-block redemption cap. */
  inventoryContract?: string
  /** Second contract holding the state that caps **deposits**, when the
   *  share token exposes no `maxDeposit()`. Yield Basis's per-market
   *  LEVAMM: the gate is `max_debt()/2 >= value_oracle().value`, and a
   *  full market reverts `"Debt too high"` with nothing to read on the
   *  share token itself. Only consumed by the `yieldbasis-lt` reader. */
  capacityContract?: string
  /** External price feed for share tokens that do not price themselves —
   *  Re Protocol's per-chain NAV oracle, Theo's RedStone `sthUSD_FUNDAMENTAL`
   *  feed on Stable. Chainlink-shaped (`latestRoundData`). Only read by the
   *  `nav-oracle` reader. */
  priceOracle?: string
  /** Decimals of `priceOracle`'s `answer`. Defaults to **18**, which is what
   *  Re's four feeds publish. Theo's RedStone feed publishes **8** — a
   *  per-feed fact with no house convention behind it, and getting it wrong
   *  is silent: the row prices at 1e-10 of par and renders as dust rather
   *  than as an error. Only read by the `nav-oracle` reader. */
  priceOracleDecimals?: number
  /** Which getter the `erc4626-cooldown` reader calls for the live exit lock.
   *  Defaults to `cooldownDuration` (StakedUSDeV2 and its forks); Theo's
   *  sthUSD spells the same dial `lockupPeriod`. */
  cooldownGetter?: 'cooldownDuration' | 'lockupPeriod'
  /**
   * The TRUST question, curated: what actually stands behind a share, and
   * where this vault sits in the loss waterfall.
   *
   * Absent ⇒ NOT ASSESSED, which is not the same as safe. Consumers see the
   * difference through `termSheet.coverage` (`present.counterparty` vs
   * `pending.counterparty`), so an assumed `overcollateralized` can never be
   * mistaken for an asserted one.
   *
   * The distinction is per-VAULT, not per-provider, and that is why it lives
   * here: Strata's senior and junior tranches are two registry rows one address
   * apart, and they are opposite sides of the same first-loss agreement.
   */
  solvency?: CounterpartyTerms['solvency']
  /** Venus Liquidity Hub topology — see {@link VenusHubConfig}. Only read by
   *  the `venus-hub` reader. */
  venusHub?: VenusHubConfig
  /** Existing intrinsic-yield fetcher whose APR is surfaced as
   *  `supplyRate`. Optional — `sDAI` for example has no dedicated
   *  fetcher today; entry still works, `supplyRate` defaults to 0. */
  yieldFetcher?: YieldFetcher
  /** Key inside the yield fetcher's `YieldResult` to read. Yield
   *  fetchers return maps keyed by symbol or by a free-text label;
   *  this is the exact key. */
  yieldKey?: string
}

/**
 * Multi-chain group — shared metadata + per-chain `{ address,
 * underlying }`. Lets us declare a vault that has independent
 * native-mint deployments on multiple chains without duplicating
 * brand / decimals / withdrawal mode per row.
 *
 * Angle stUSD/stEUR use deterministic deployments so the share
 * address is the same vanity string on every chain (only the
 * underlying reserve differs). yUSD ships independent ERC-4626
 * deployments per chain with distinct addresses.
 *
 * See [TIMELOCKS_AND_CHAINS.md](./TIMELOCKS_AND_CHAINS.md) for the
 * verification matrix.
 */
interface MultiChainSavingsGroup {
  base: Omit<SavingsRegistryEntry, 'address' | 'underlying'>
  chains: {
    [chainId: string]: {
      address: string
      underlying: string
    } & Partial<Omit<SavingsRegistryEntry, 'address' | 'underlying'>>
  }
}

/** Angle stUSD — same vanity share address on every chain via
 *  Angle's deterministic deployment. Underlying is USDA, also a
 *  vanity address. Per-chain reserves are independent. */
const stUsdGroup: MultiChainSavingsGroup = {
  base: {
    symbol: 'stUSD',
    brand: 'Angle',
    description:
      "USDA is Angle's overcollateralised USD stablecoin; stUSD stakes it and accrues a governance-managed savings rate funded by the yield on Angle's reserves (RWAs and DeFi lending). Instant permissionless stake and unstake.",
    decimals: 18,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant',
    yieldFetcher: angleFetcher,
    yieldKey: 'Angle Staked USDA::stUSD',
  },
  chains: {
    '1': {
      address: '0x0022228a2cc5e7ef0274a7baa600d44da5ab5776',
      underlying: '0x0000206329b97db379d5e1bf586bbdb969c63274',
    },
    '42161': {
      address: '0x0022228a2cc5e7ef0274a7baa600d44da5ab5776',
      underlying: '0x0000206329b97db379d5e1bf586bbdb969c63274',
    },
    '10': {
      address: '0x0022228a2cc5e7ef0274a7baa600d44da5ab5776',
      underlying: '0x0000206329b97db379d5e1bf586bbdb969c63274',
    },
    '8453': {
      address: '0x0022228a2cc5e7ef0274a7baa600d44da5ab5776',
      underlying: '0x0000206329b97db379d5e1bf586bbdb969c63274',
    },
    '59144': {
      address: '0x0022228a2cc5e7ef0274a7baa600d44da5ab5776',
      underlying: '0x0000206329b97db379d5e1bf586bbdb969c63274',
    },
  },
}

/**
 * Angle stEUR — same vanity address as stUSD, underlying is EURA.
 *
 * The EURA address is PER CHAIN (it is not a vanity deployment), and until
 * 2026-09-18 every row here carried `0x1aBa…` — which is Circle's EURC, not
 * EURA, on all four chains. Each `underlying` below is the vault's own
 * `asset()`, read on-chain; the Erc4626Composite roster parity test pins it.
 */
const stEurGroup: MultiChainSavingsGroup = {
  base: {
    symbol: 'stEUR',
    brand: 'Angle',
    description:
      "EURA is Angle's overcollateralised euro stablecoin; stEUR stakes it and accrues a governance-managed savings rate funded by the yield on Angle's reserves. Instant permissionless stake and unstake.",
    decimals: 18,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant',
    yieldFetcher: angleFetcher,
    yieldKey: 'Angle Staked EURA::stEUR',
  },
  chains: {
    '1': {
      address: '0x004626a008b1acdc4c74ab51644093b155e59a23',
      underlying: '0x1a7e4e63778b4f12a199c062f3efdd288afcbce8',
    },
    '42161': {
      address: '0x004626a008b1acdc4c74ab51644093b155e59a23',
      underlying: '0xfa5ed56a203466cbbc2430a43c66b9d8723528e7',
    },
    '100': {
      address: '0x004626a008b1acdc4c74ab51644093b155e59a23',
      underlying: '0x4b1e2c2762667331bc91648052f646d1b0d35984',
    },
    '59144': {
      address: '0x004626a008b1acdc4c74ab51644093b155e59a23',
      underlying: '0x1a7e4e63778b4f12a199c062f3efdd288afcbce8',
    },
  },
}

/** YieldFi yUSD — independent ERC-4626 over USDC per chain. Note the
 *  18-decimal share over 6-decimal USDC convention. */
const yUsdGroup: MultiChainSavingsGroup = {
  base: {
    symbol: 'yUSD',
    brand: 'YieldFi',
    description:
      "YieldFi's managed USD vault over USDC: deposits are allocated to curated DeFi and CeDeFi strategies and yield accrues into the share price (rewards vest over ~24h to smooth the curve). Instant exit.",
    // 18-decimal share over 6-decimal USDC — see TIMELOCKS_AND_CHAINS.md.
    decimals: 18,
    underlyingDecimals: 6,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant',
    yieldFetcher: yusdFetcher,
    yieldKey: 'YieldFi yUSD::yUSD',
  },
  chains: {
    '1': {
      address: '0x19ebd191f7a24ece672ba13a302212b5ef7f35cb',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    },
    '42161': {
      address: '0x4772d2e014f9fc3a820c444e3313968e9a5c8121',
      underlying: '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
    },
    // Base address `0x895e15020c3f...79` was flagged unverified by
    // VAULT_READ_INTERFACES.md research; the live read returned no
    // data (not deployed, or not an ERC-4626 surface). Re-add once
    // confirmed on Basescan.
  },
}

/**
 * Spark Savings **V1** — `sUSDC`, five chains.
 *
 * Not really a vault of its own: `UsdcVault`/`UsdcVaultL2` holds sUSDS
 * and routes both legs through Spark's PSM3, so a share *is* an sUSDS
 * and `convertToAssets` is just `psm.previewSwapExactIn(susds, usdc, …)`.
 * Three consequences, all load-bearing here:
 *
 *  1. **18-decimal shares over 6-decimal USDC** (the share is an sUSDS),
 *     hence `underlyingDecimals: 6` — the yUSD case again.
 *  2. **The yield is the Sky Savings Rate verbatim**, identical on every
 *     chain, so all five rows share ONE rate — `sparkSavingsFetcher`
 *     reads it straight off mainnet sUSDS' `ssr()`. Live check: the
 *     share price reads 1.104891 on all five deployments
 *     simultaneously, matching mainnet sUSDS `chi()`.
 *  3. **Exits are capped by the PSM3 pocket's USDC**, not by the vault —
 *     `maxRedeem` clamps to it. Coverage measured 2200 % on Ethereum
 *     (a dedicated $4.19B pocket) but only 6 % on Arbitrum, 14 % on
 *     Base, 26 % on OP and 29 % on Unichain, where the pocket *is* the
 *     PSM3. Reporting `totalAssets` as withdrawable would overstate
 *     Arbitrum by 17x, so these use the `erc4626-idle` reader with the
 *     pocket pinned as `inventoryContract`.
 */
const sUsdcGroup: MultiChainSavingsGroup = {
  base: {
    reader: 'erc4626-idle',
    symbol: 'sUSDC',
    brand: 'Spark',
    description:
      "Not a standalone vault: a share is sUSDS held via Spark's PSM3, so sUSDC earns the Sky Savings Rate verbatim with USDC in and out routed through the PSM. Exits settle instantly but are capped by the PSM pocket's USDC balance — deep on Ethereum, materially thinner on the L2 deployments.",
    decimals: 18,
    underlyingDecimals: 6,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant-capped',
    yieldFetcher: sparkSavingsFetcher,
    yieldKey: SPARK_SUSDC_KEY,
  },
  chains: {
    '1': {
      address: '0xbc65ad17c5c0a2a4d159fa5a503f4992c7b545fe',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
      // Mainnet PSM keeps a dedicated pocket contract.
      inventoryContract: '0x37305b1cd40574e4c5ce33f8e8306be057fd7341',
    },
    '8453': {
      address: '0x3128a0f7f0ea68e7b7c9b00afa7e41045828e858',
      underlying: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
      // L2 PSM3s custody the pocket themselves (`pocket() == psm`).
      inventoryContract: '0x1601843c5e9bc251a3272907010afa41fa18347e',
    },
    '42161': {
      address: '0x940098b108fb7d0a7e374f6eded7760787464609',
      underlying: '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
      inventoryContract: '0x2b05f8e1cacc6974fd79a673a341fe1f58d27266',
    },
    '10': {
      address: '0xcf9326e24ebffbef22ce1050007a43a3c0b6db55',
      underlying: '0x0b2c639c533813f4aa9d7837caf62653d097ff85',
      inventoryContract: '0xe0f9978b907853f354d79188a3defbd41978af62',
    },
    '130': {
      address: '0x14d9143becc348920b68d123687045db49a016c6',
      underlying: '0x078d782b760474a361dda0af3839290b0ef57ad6',
      inventoryContract: '0x7b42ed932f26509465f7ce3faf76ffce1275312f',
    },
  },
}

/**
 * Re Protocol **reUSD** — a reinsurance-backed USD token, four chains.
 *
 * Not a vault in any on-chain sense: the token is a bare ERC-20 on every
 * deployment (`asset()`, `totalAssets()`, `convertToAssets()` and every
 * rebase getter revert), and the accrued price lives in a separate
 * Chainlink-shaped **NAV oracle** that Re re-publishes daily at 00:00
 * UTC. Hence the `nav-oracle` reader. The oracle answers the same price
 * on all four chains (verified simultaneously), so one APR covers them.
 *
 * `request-based` is a deliberate under-statement of the exit. Re runs
 * an instant leg — 6 bps fee, capped at 10 % per transaction and 20 % of
 * capacity daily — but only while the redemption buffer exceeds 1 % of
 * supply, and below that everything drops into a **quarterly** queue.
 * The buffer is held in bridged sUSDe on Ethereum/Base/Arbitrum, which
 * exposes no local `convertToAssets` outside Ethereum, so its USD value
 * cannot be read on the chains that need it (measured today: ~$9.9M
 * against $169M on Ethereum, ~$1.26M against $615k on Base, dust on
 * Arbitrum, and $191 against $492k on Avalanche — i.e. already
 * below the 1 % threshold there). Reporting `liquidity: 0` never
 * overstates; upgrading it needs a cross-chain sUSDe rate.
 *
 * `isMintable: false` is VERIFIED, not conservative: minting goes through
 * Re's per-chain **Insurance Capital Layer**, whose `deposit(...)` is
 * `onlyKYCUser` — `kycRegistry.isKYCApproved(msg.sender)` or revert
 * `OnlyApprovedKYCUser()`. Re's own docs describe the same flow (deposit to
 * escrow -> complete KYC -> tokens minted on approval, under a Token Purchase
 * Agreement, eligible non-U.S. persons only). It is a pure KYC gate rather
 * than a shutdown — `depositEnabled()` true, `paused()` false, and
 * `previewDeposit` quotes — so there is nothing here to wait for.
 *
 * Which is why the rows carry `secondaryMarketOnly`: the entry that exists
 * for OUR users is the market, and Re's docs put it at the centre of their
 * "DeFi-native core". Curve reUSD/USDC on Ethereum is the deepest venue
 * (also Fluid reUSD/USDT and Pendle PT/YT); the `ShareToken` implementation
 * has no transfer hook of any kind, so holding and trading are unrestricted;
 * and a $100k round trip quoted ~0.3 bps off par at integration — INSIDE the
 * mint, before its fee. Redemption mirrors the mint: role-gated through Re's
 * AccessManager, so a non-KYC holder's exit is the sale, never the quarterly
 * window described below.
 *
 * Distribution is lopsided: 99.4 % of supply is on Ethereum.
 */
const reUsdGroup: MultiChainSavingsGroup = {
  base: {
    reader: 'nav-oracle',
    symbol: 'reUSD',
    solvency: 'tranched-senior',
    brand: 'Re Protocol',
    description:
      "Senior tranche of Re's reinsurance capital stack: deposits back insurance-linked programs and earn underwriting premium (the junior reUSDe absorbs losses first). A bare ERC-20 priced by a daily NAV oracle. Minting and redeeming at NAV need Re's KYC approval; without it the position is entered and exited by TRADING reUSD (deepest venue: Curve reUSD/USDC on Ethereum), at whatever discount or premium to NAV the market pays. For KYC'd holders redemption is instant (small fee) while the buffer holds, otherwise it queues — quarterly in the worst case.",
    decimals: 18,
    underlyingDecimals: 6,
    isRebasing: false,
    isMintable: false,
    secondaryMarketOnly: true,
    withdrawalMode: 'request-based',
    // Quarterly queue — the documented worst case, and the live case
    // wherever the buffer sits under 1 % of supply. It describes the KYC
    // holder's exit; ours is the sale (`secondaryMarketOnly`).
    withdrawalCooldownSeconds: 90 * 86_400,
    yieldFetcher: reProtocolFetcher,
    yieldKey: REUSD_KEY,
  },
  chains: {
    // TRAP, per chain: the ICL is a DIFFERENT contract from the token, and
    // the address that looks like a queue is not one. Re's address book
    // labels `0x5c454f55…` and its siblings "Daily Instant Redemption Vault
    // — payout token custody", which is `inventoryContract`'s definition,
    // not `withdrawQueue`'s. Only Ethereum publishes an actual delayed-
    // redemption module (`WindowRedemption`, the quarterly window); the
    // other three name none, so they carry none rather than a plausible
    // address a caller might try to request against.
    '1': {
      address: '0x5086bf358635b81d8c47c66d1c8b9e567db70c72',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      priceOracle: '0x72b5760cfbe437dd01409f44055fdfb8f8121b46',
      mintContract: '0x4691c475be804fa85f91c2d6d0adf03114de3093', // ICL
      withdrawQueue: '0xd2e077d945ec77b45fbe4622e01f4c79e4ba389a', // WindowRedemption
      inventoryContract: '0x5c454f5526e41fbe917b63475cd8ca7e4631b147',
    },
    '8453': {
      address: '0x7d214438d0f27afccc23b3d1e1a53906ace5cfea',
      underlying: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
      priceOracle: '0x67a3226e69a1a8316ef1807a44f077af80071926',
      // NOT the token — that was wrong. `0x7d214438…` is a plain
      // `ShareToken`: role-gated `mint(address,uint256)`, no `kycRegistry()`,
      // no `deposit` of any arity. Base's ICL is its own contract, and
      // answers `kycRegistry() = 0xd4326b16…` / `shareToken() = 0x7d214438…`.
      mintContract: '0xd75ea2fd3d00399df7b7241ab7a189085ab2ede9',
      inventoryContract: '0x9ab62aebabe738ab233c447eedce88d1d0a61fe3',
    },
    '42161': {
      address: '0x76ce01f0ef25aa66cc5f1e546a005e4a63b25609',
      underlying: '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
      priceOracle: '0x48abcc5a711ac23d3730bf627415dc898cbc5967',
      mintContract: '0x802edbb1ec20548a4388abc337e4011718eb0291',
      inventoryContract: '0xfd4016ea13ca8acc04a11a99702df076a4d3b852',
    },
    '43114': {
      address: '0x180af87b47bf272b2df59dccf2d76a6eafa625bf',
      underlying: '0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e',
      priceOracle: '0x4c558694f16484e5c7a4a52bd210d471860ce7bc',
      mintContract: '0xb22a8533e6cd81598f82514a42f0b3161745fbe1',
      inventoryContract: '0xe13292f97e38da0c64398de5e0bfc95180de9d23',
    },
    // NB: our token list also carries reUSD on BNB (0xba9425ec…) and Ink
    // (0x5bcf6b00…), but Re publishes no NAV oracle for either, so there
    // is no price source and they are deliberately NOT registered.
  },
}

/** Parallel sUSDp — ERC-4626 savings vault over Parallel V3's USDp
 *  stablecoin (Angle-lineage `Savings.sol`; docs.parallel.best). Yield
 *  is keeper-minted USDp at a governance `rate()` — no on-chain
 *  solvency invariant on the savings leg (Spark-V2 `vsr` trust class);
 *  backing sits in the Parallelizer reserves. Plain instant 4626 exit,
 *  no cooldown or fee, unpaused (verified 2026-08-03). Independent
 *  deployments per chain, TVL 268k / 42k / 2.9k / ~130 USDp — small,
 *  but sUSDp is already Silo V2 collateral on Avalanche, so the
 *  intrinsic yield join matters beyond the vault rows. Token-list
 *  group `Staked USDp::sUSDp` is consistent on every chain.
 *
 *  Two traps (verified 2026-08-03): the deployer reuses the SAME
 *  address for DIFFERENT contracts per chain — `0x9b3a8f7…` is sUSDp
 *  on HyperEVM but USDp (the underlying!) on Ethereum — so never
 *  copy a row across chains unverified. And the live Sonic (146)
 *  vault `0xe8a3da…` is deliberately NOT registered: its per-chain
 *  `rate()` is 0 (~1 USDp TVL), and the shared yield key would
 *  overstate it at the active-chain rate. */
const sUsdpGroup: MultiChainSavingsGroup = {
  base: {
    symbol: 'sUSDp',
    brand: 'Parallel',
    description:
      "USDp is Parallel's (Angle-lineage) stablecoin; sUSDp accrues yield as keeper-minted USDp at a governance-set rate, with backing held in the Parallelizer reserves rather than enforced by an on-chain solvency invariant. Instant permissionless exit.",
    decimals: 18,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant',
    yieldFetcher: parallelFetcher,
    yieldKey: SUSDP_KEY,
  },
  chains: {
    '43114': {
      address: '0x9d92c21205383651610f90722131655a5b8ed3e0',
      underlying: '0x9ee1963f05553ef838604dd39403be21cef26aa4', // USDp
    },
    '999': {
      address: '0x9b3a8f7cec208e247d97dee13313690977e24459',
      underlying: '0xbe65f0f410a72bec163dc65d46c83699e957d588',
    },
    '1': {
      address: '0x0d45b129dc868963025db79a9074ea9c9e32cae4',
      // Yes, the HyperEVM sUSDp address — see the address-reuse trap
      // in the group comment.
      underlying: '0x9b3a8f7cec208e247d97dee13313690977e24459',
    },
    '8453': {
      address: '0x472ed57b376fe400259fb28e5c46eb53f0e3e7e7',
      underlying: '0x76a9a0062ec6712b99b4f63bd2b4270185759dd5',
    },
  },
}

/** OpenEden cUSDO — permissionless ERC-4626 over USDO, OpenEden's
 *  rebasing T-bill yieldcoin (the 2026-08 RWA sweep's one full-vault
 *  candidate: unlike USYC/BUIDL/USTB the wrapper itself is open —
 *  `maxDeposit = uint256.max` verified on all three chains). USDO
 *  mint/redeem against fiat is KYC at OpenEden, but secondary USDO
 *  transfer and the cUSDO wrap/unwrap are free, so `isMintable: true`
 *  with USDO as the entry asset. The underlying REBASES (wstUSR
 *  precedent) — the wrapper share accrues instead. Yield rides the
 *  shared `rwaFundsFetcher` (DefiLlama `openeden-usdo`).
 *
 *  Address-reuse trap (same defect class as Parallel sUSDp):
 *  `0xad55aebc9b…` is cUSDO on Ethereum but USDO on Base. Plasma
 *  (9745) carries cUSDO in the token list but the contract answered
 *  nothing on RPC probes — not registered. */
const cUsdoGroup: MultiChainSavingsGroup = {
  base: {
    symbol: 'cUSDO',
    solvency: 'nav-attested',
    brand: 'OpenEden',
    description:
      "USDO is OpenEden's rebasing, T-bill-backed yieldcoin (minting against fiat is KYC-gated at OpenEden); cUSDO is the permissionless non-rebasing wrapper that turns the rebase into an appreciating share price. Instant wrap and unwrap.",
    decimals: 18,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant',
    yieldFetcher: rwaFundsFetcher,
    yieldKey: 'Compounding Open Dollar::CUSDO',
  },
  chains: {
    '1': {
      address: '0xad55aebc9b8c03fc43cd9f62260391c13c23e7c0',
      underlying: '0x8238884ec9668ef77b90c6dff4d1a9f4f4823bfe', // USDO
    },
    '56': {
      address: '0x64748ea3e31d0b7916f0ff91b017b9f404ded8ef',
      underlying: '0x302e52aff9815b9d1682473dbfb9c74f9b750aa8',
    },
    '8453': {
      address: '0x83db73ef5192de4b6a4c92bd0141ba1a0dc87c65',
      underlying: '0xad55aebc9b8c03fc43cd9f62260391c13c23e7c0',
    },
  },
}

/** USDD sUSDD — an sDAI clone over USDD 2.0, the Maker-fork stablecoin
 *  from the TRON ecosystem (docs.usdd.io). Plain instant permissionless
 *  4626 (`maxDeposit = uint256.max`, no cooldown or fee — verified
 *  on-chain 2026-08-04); the share prices itself off a per-chain `Pot`
 *  (`pot()` on the share token) whose `dsr()` is the USDD Savings Rate.
 *  Ethereum and BNB are INDEPENDENT Maker stacks — independent `chi`
 *  (1.0609 vs 1.0534 at integration) and per-chain `dsr` — hence the
 *  chain-scoped yield keys. Supply split ~220M/11M ETH/BNB, both pots
 *  at 4.00 % APY (3.92 % nominal APR) at integration.
 *
 *  Underlying trap: USDD 2.0's on-chain `name()` DIFFERS per chain
 *  ("Usdd Stablecoin" on Ethereum, "Decentralized USD" on BNB) and two
 *  legacy v1 tokens still carry the USDD ticker on each chain — the
 *  2.0 deployments here are the ones from docs.usdd.io's
 *  deployment-addresses page, cross-checked as `sUSDD.asset()`. */
const sUsddGroup: MultiChainSavingsGroup = {
  base: {
    symbol: 'sUSDD',
    brand: 'USDD',
    description:
      'USDD 2.0 is the TRON-ecosystem, Maker-fork USD stablecoin; sUSDD is an sDAI-style vault accruing the USDD Savings Rate from its per-chain Pot. Instant exit; the Ethereum and BNB stacks are independent, with independent rates.',
    decimals: 18,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant',
    yieldFetcher: usddFetcher,
  },
  chains: {
    '1': {
      address: '0xc5d6a7b61d18afa11435a889557b068bb9f29930',
      underlying: '0x4f8e5de400de08b164e7421b3ee387f461becd1a', // USDD 2.0
      yieldKey: susddKey('1'),
    },
    '56': {
      address: '0x8ba9da757d1d66c58b1ae7e2ed6c04087348a82d',
      underlying: '0x45e51bc23d592eb2dba86da3985299f7895d66ba', // USDD 2.0
      yieldKey: susddKey('56'),
    },
  },
}

const MULTI_CHAIN_GROUPS: MultiChainSavingsGroup[] = [
  stUsdGroup,
  stEurGroup,
  yUsdGroup,
  sUsdcGroup,
  reUsdGroup,
  sUsdpGroup,
  cUsdoGroup,
  sUsddGroup,
]

/**
 * Shared metadata for a Spark Vaults **V2** row (`sp*`).
 *
 * A different product from `sUSDC` above despite the shared brand: its
 * own `chi`/`rho`/`vsr` accumulator, funded by lending the deposits out
 * through the Spark Liquidity Layer via `take()` (TAKER_ROLE). That
 * makes `totalAssets()` a **liability** — literally
 * `convertToAssets(totalSupply)` — with `assetsOutstanding()` the part
 * no longer on the contract. There is no on-chain solvency invariant and
 * `vsr` is an admin-pushed rate bounded by `minVsr`/`maxVsr`, so these
 * carry the same trust assumption as Maple `syrupUSDC` and Native wNLP.
 *
 * `maxDeposit` is `type(uint256).max` (uncapped, contrary to the
 * integrator docs) but `maxWithdraw`/`maxRedeem` clamp to the vault's own
 * idle balance — 3.4 % of TVL on mainnet spUSDC at the time of writing.
 * Hence `erc4626-idle` + `instant-capped` with no `inventoryContract`
 * override: the vault *is* the inventory. Above that cap, exits go
 * through Spark's withdrawal-intents contract, typically settled in
 * minutes; there is no fixed on-chain window to pin, so
 * `withdrawalCooldownSeconds` is deliberately left unset.
 */
const sparkV2Base = (symbol: string, decimals: number) =>
  ({
    reader: 'erc4626-idle',
    symbol,
    brand: 'Spark',
    // `totalAssets()` is a LIABILITY (a claim on Spark, not cash in the
    // contract) with no on-chain solvency invariant — the same trust class the
    // description spells out, and the same one Maple and Native carry.
    solvency: 'undercollateralized' as const,
    description:
      "Spark Vaults V2: deposits are lent out through the Spark Liquidity Layer and accrue an admin-set vault savings rate — the vault's assets are a claim on Spark, not cash in the contract, with no on-chain solvency invariant. Exits are instant up to the vault's idle balance; anything larger settles through Spark's withdrawal intents, typically within minutes.",
    decimals,
    underlyingDecimals: decimals,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'instant-capped',
    yieldFetcher: sparkSavingsFetcher,
  }) satisfies Partial<SavingsRegistryEntry>

/** Spark's mainnet large-withdrawal intents contract (source-verified,
 *  5,045 bytes of code) — where an exit above the idle balance queues. */
const SPARK_V2_WITHDRAW_INTENTS = '0x592b7db9906e6f8924c4d74c2a0ab86ce44fdddf'

/**
 * Frankencoin ZCHF savings module — one row per chain that runs one.
 *
 * NOT a vault and NOT a token: `save(owner, amount)` books ZCHF into an
 * internal `savings(address)` account and `withdraw(target, amount)`
 * pays it back out, so the position is a balance that grows in place
 * (`isRebasing: true`, `exchangeRate` pinned at par) and the module's
 * own ZCHF balance is the whole book. Hence the bespoke
 * `frankencoin-savings` reader.
 *
 * `symbol: 'ZCHF'` is deliberate — the honest ticker for a position
 * denominated 1:1 in ZCHF is ZCHF. Inventing `sZCHF` would name a token
 * that does not exist, and `svZCHF` is a real, different thing (see
 * below). But a ticker is not a name, and NAMING the row from it is what
 * put two rows called "ZCHF" on Ethereum — this module and the svZCHF
 * wrapper below it — distinguishable only by an address suffix a listing
 * had to bolt on. Hence `label`: the row is the savings module, so it is
 * called that, and svZCHF keeps its own ticker.
 *
 * Exit is instant and never short (withdrawals are paid from the
 * module's own balance), but the YIELD is not instant: `INTEREST_DELAY`
 * is 3 days and a top-up re-weights the account's tick counter, so
 * exiting early can realise ~0 %.
 *
 * **Do not add an `svZCHF` row on Gnosis without netting it out.**
 * `0x6165946250dd04740ab1409217e95a4f38374fe9` is a genuine ERC-4626
 * over the bridged ZCHF — but it holds zero idle ZCHF and deposits
 * everything straight into the Gnosis module, where it accounted for
 * 789,231 of that module's 1,257,396 ZCHF at 2026-08-12. Registering
 * both rows would report 2.05M against 1.26M of real money. The module
 * is the row; the wrapper is a route into it.
 */
const frankencoinSavingsBase = {
  reader: 'frankencoin-savings',
  symbol: 'ZCHF',
  label: 'Savings Module',
  brand: 'Frankencoin',
  description:
    "ZCHF is Frankencoin's decentralised Swiss-franc stablecoin, minted against collateral in permissionless, auction-policed positions. The savings module pays a governance-set rate funded by borrower interest: deposits are never lent on (they sit segregated inside the module) and interest is minted by the protocol into your balance, so there are no shares and no share price. Withdrawals are instant and uncapped, but interest only starts accruing after 3 days and a top-up restarts that clock pro-rata, so a short stay can earn nothing.",
  decimals: 18,
  isRebasing: true,
  isMintable: true,
  withdrawalMode: 'instant',
  // The exit is instant; the YIELD is not. `INTEREST_DELAY` is 3 days and a
  // top-up re-weights the whole position's clock, so a short stay earns zero.
  yieldWarmupSeconds: 3 * 86_400,
  // `Δticks × saved / 1e6 / 365 days` — linear, compounding only when someone
  // calls `refresh`.
  accrual: 'linear',
  // The module is a registered ZCHF minter, so the token already grants it an
  // implicit infinite allowance — verified on all three chains.
  needsDepositApproval: false,
  // NOT a token: `balanceOf` reverts on the module, so a user's position has
  // to be read from `savings(account)` + `accruedInterest(account)`.
  balanceKind: 'savings-account' as const,
  // Not an assumption: deposits are never lent on. They sit segregated inside
  // the module (`totalAssets` IS the module's own ZCHF balance) and the exit
  // is paid from it, so the PRINCIPAL is fully reserved rather than merely
  // over-collateralised. What is a claim on the protocol is the INTEREST,
  // which is minted on refresh — stated in the description.
  solvency: 'overcollateralized',
  yieldFetcher: frankencoinSavingsFetcher,
} satisfies Partial<SavingsRegistryEntry>

/**
 * Yield Basis `yb-<ASSET>` markets (Ethereum only, docs.yieldbasis.com).
 *
 * A market takes BTC or ETH, borrows a matching amount of crvUSD against
 * a Curve-DAO credit line, mints Cryptoswap LP and holds it in a
 * constant-`L=2` AMM — giving **2× leveraged LP exposure with
 * impermanent loss cancelled**, so the share tracks its asset 1:1 (full
 * downside included) and trading fees are the yield.
 *
 * These are the **only four current markets**: `Factory.market_count()`
 * is 11 and indices 0–6 are superseded generations that are withdraw-only
 * yet still hold ~$32M and still report a rate, so they are deliberately
 * unregistered. All eleven share the `yb-<ASSET>` symbol — key on address,
 * never on symbol.
 *
 * Three deliberate choices worth defending:
 *
 * - **`yieldProfile: 'volatile'`** (set via `VOLATILE_VAULT_OVERRIDES`).
 *   The share price *falls* in asset terms whenever rebalance slippage
 *   outruns fees; three of the four printed a negative trailing-30d rate
 *   at integration. This is the Hyperliquid-HLP class, not the sUSDe one.
 * - **`isMintable: true` with a real `depositCapacity`.** Deposits are
 *   permissionless — there is no allowlist — but they are capped by
 *   `AMM.max_debt()/2 >= value` with no `maxDeposit()` to read. At
 *   integration the four markets were 77.8 / 99.5 / 96.7 / 103.4 % full,
 *   i.e. **WETH already reverts**. `isMintable` states the permission;
 *   `depositCapacity` states the room. Do not conflate them.
 * - **`withdrawalMode: 'instant'`.** Exits are permissionless, uncapped
 *   and same-block, with no fee and no cooldown. What varies is the
 *   *price*: the redeemable value carries the protocol's TRD, and that is
 *   already inside `exchangeRate` rather than modelled as a fee.
 *
 * The gauge (`LiquidityGauge`, the strict-ERC-4626 surface) is
 * deliberately NOT registered — staking forfeits PPS growth and pays YB
 * emissions instead, so its share price declines by construction. See
 * [readers/yieldBasisLt.ts](./readers/yieldBasisLt.ts).
 */
const ybMarket = (
  symbol: string,
  underlying: string,
  underlyingDecimals: number,
  address: string,
  capacityContract: string,
  yieldKey: string,
): SavingsRegistryEntry => ({
  reader: 'yieldbasis-lt',
  address,
  underlying,
  capacityContract,
  symbol,
  brand: 'Yield Basis',
  description: `${symbol.replace('yb-', '')} in a 2×-leveraged Curve Cryptoswap LP funded by borrowed crvUSD, with impermanent loss cancelled — the share tracks the asset 1:1 (full downside included) and earns the pool's trading fees. Not a yield-accrual vault: the share price falls in asset terms whenever rebalancing cost outruns fees, so the rate is regularly negative. Exit is instant and permissionless at a live unwind price a few basis points off the oracle value (the protocol's Temporary Redemption Discount). Deposits are permissionless but protocol-capped — read the deposit capacity, which is often zero.`,
  // LT shares are always 18-decimal, independent of the underlying.
  decimals: 18,
  underlyingDecimals,
  isRebasing: false,
  isMintable: true,
  withdrawalMode: 'instant',
  yieldFetcher: yieldBasisFetcher,
  yieldKey,
})

/** Yield Basis market index → `{ LT, LEVAMM }`, verified on-chain
 *  2026-08-10 against `docs.yieldbasis.com/user/reference/contract-addresses`. */
const YIELD_BASIS_ENTRIES: SavingsRegistryEntry[] = [
  // idx 7 — the only market with meaningful headroom at integration
  // (~110 WBTC / $7.1M of $32.1M capacity free).
  ybMarket(
    'yb-WBTC',
    '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
    8,
    '0x651d4b8168488fa163d85304662e8278d4c55baa',
    '0x7b9817eb5c49a99875138a5d52bf64a8b2cffffe',
    YB_WBTC_KEY,
  ),
  // idx 8 — largest market ($42.2M) and 99.5 % full.
  ybMarket(
    'yb-cbBTC',
    '0xcbb7c0000ab88b473b1f5afd9ef808440eed33bf',
    8,
    '0x722fc3640ba007c3e9867ccdb0dca59f2e2f29f9',
    '0x49f51d7e279252f3c9a09678fdc65b4dbd5cb196',
    YB_CBBTC_KEY,
  ),
  // idx 9
  ybMarket(
    'yb-tBTC',
    '0x18084fba666a33d37592fa2633fd49a74dd93a88',
    18,
    '0x771f7290428d830ecd41e980745c327e507823ec',
    '0x0e357af536592f275c6aa07b8ac2ca6eb3d0cf5a',
    YB_TBTC_KEY,
  ),
  // idx 10 — over cap at integration; `depositCapacity` reads 0.
  ybMarket(
    'yb-WETH',
    '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
    18,
    '0x2b9c9f3bdceb5d8e36a4704f08a78fca53343cea',
    '0x5f8d24f33cc5a1d5d1bf012261e6a2214c92233c',
    YB_WETH_KEY,
  ),
]

/**
 * Single-chain savings entries. Keyed by chainId (string — matches
 * the rest of the codebase). Multi-chain assets are defined in
 * `MULTI_CHAIN_GROUPS` above and merged in at module load.
 */
const SINGLE_CHAIN_ENTRIES: { [chainId: string]: SavingsRegistryEntry[] } = {
  '1': [
    ...YIELD_BASIS_ENTRIES,
    {
      address: '0x9d39a5de30e57443bff2a8307a4256c8797a3497',
      underlying: '0x4c9edd5852cd905f086c759e8383e09bff1e68b3',
      symbol: 'sUSDe',
      brand: 'Ethena',
      description:
        "USDe is Ethena's synthetic dollar — staked-ETH/BTC collateral hedged with short perpetual futures (delta-neutral). sUSDe stakes it and accrues the basis and staking revenue via an appreciating share price; unstaking requires a cooldown (7 days pinned here, governance-mutable).",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'fixed-cooldown',
      // Governance-mutable + dynamic since Mar 2026. Pinned to the
      // pre-2026 7-day baseline; re-read on-chain for the precise
      // current value.
      withdrawalCooldownSeconds: 7 * 86400,
      yieldFetcher: susdeFetcher,
      yieldKey: 'SUSDE',
    },
    {
      // Tori strUSD — StakedUSDeV2 clone over trUSD (delta-neutral
      // synthetic dollar). Cooldown verified on-chain 2026-08:
      // `cooldownDuration() = 604800`, silo = 0xF7c0d885…, direct
      // withdraw/redeem disabled while active.
      address: '0x280839980a7ed0d7717f64125fe241012e5f5815',
      underlying: '0xd0580192e98ea6ceb9c7b6191ed2e27560911697', // trUSD
      symbol: 'strUSD',
      brand: 'Tori',
      description:
        "trUSD is Tori's delta-neutral synthetic dollar — capital runs market-neutral money-market, futures-basis and calendar-spread strategies with off-chain custodians. strUSD stakes it with an appreciating share price (a 10% performance fee is already netted in); unstaking requires a 7-day cooldown claimed through the trUSD Silo.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 7 * 86400,
      yieldFetcher: toriFetcher,
      yieldKey: STRUSD_KEY,
    },
    {
      // Neutrl sNUSD — StakedUSDeV2 clone over NUSD (market-neutral
      // OTC-discount arbitrage + funding-rate synthetic dollar).
      // Verified on-chain 2026-08: `cooldownDuration() = 864000` (10d,
      // governance-mutable), `vestingPeriod() = 604800`, maxWithdraw = 0
      // while active, exits park in the NUSD Silo 0x6cdfc009… and claim
      // via `unstake`. A docs-listed InstantUnstaking contract
      // (0x4Bb8F67D…, 1% fee) exists but held ~2k NUSD against $35M TVL
      // at integration — dust coverage, deliberately not modeled;
      // revisit as `fee-or-queued` if Neutrl ever funds the buffer.
      // Arbitrum/Plasma carry same-address LayerZero OFT bridges whose
      // 4626 surface reverts (bare ERC-20s) — Ethereum-only row.
      address: '0x08efcc2f3e61185d0ea7f8830b3fec9bfa2ee313',
      underlying: '0xe556aba6fe6036275ec1f87eda296be72c811bce', // NUSD
      symbol: 'sNUSD',
      brand: 'Neutrl',
      description:
        "NUSD is Neutrl's market-neutral synthetic dollar — capital runs hedged OTC-discount arbitrage (buying locked altcoin positions at a discount, shorting the exposure) plus funding-rate strategies. sNUSD stakes it with an appreciating share price; rewards vest linearly over 7 days and unstaking requires a 10-day cooldown claimed through the NUSD Silo.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 10 * 86400,
      yieldFetcher: snusdFetcher,
      yieldKey: SNUSD_KEY,
    },
    {
      // Brix wiTRY — StakedUSDeV2 clone over iTRY, a TURKISH LIRA stablecoin
      // backed by the Digital Liquidity Fund's DLF token (shares in a
      // regulated basket of Turkish money-market funds). Yield is the funds'
      // NAV delta, minted as fresh iTRY on a scheduled cycle (weekly at
      // launch) and `transferInRewards`'d into the vault, where it vests
      // linearly over a PRIVATE 248,400 s (69 h) constant — see
      // `fetchers/brix.ts` for how that was solved, and why the rate is not
      // read from a getter.
      //
      // THE RATE IS IN LIRA. 36-38 % is what Turkish money-market paper pays
      // BECAUSE the lira depreciates; a USD holder earns that minus the
      // TRY/USD drift, and historically the two have been close. Nothing in
      // the numeric fields can express that — `supplyRate` is asset-
      // denominated for every row on this surface — so the description below
      // is the only place it is stated, and it must stay there. Note also
      // that `denomination` classifies iTRY as `stable` (fiat-pegged, the
      // same call already made for ZCHF / EURA / TRYB): that axis means
      // "pegged to a currency", never "does not move against the dollar".
      //
      // Verified on-chain 2026-08-14: `silo()` = 0x1b301c81…, `unstake`,
      // `cooldownAssets`/`cooldownShares`, `MAX_COOLDOWN_DURATION` = 90 d,
      // `maxDeposit` = uint.max.
      //
      // `isMintable: false` — NOT because of a cap or a KYC gate on the vault,
      // which has neither, but because **the UNDERLYING is a permissioned
      // token**. iTRY is an Ethena `USDtb` clone sitting at
      // `transferState() == 1` (WHITELIST_ENABLED), whose transfer hook needs
      // BOTH `from` AND `to` to hold `WHITELISTED_ROLE` and otherwise reverts
      // `OperationNotAllowed()`. The role is held only by protocol plumbing
      // (this vault, the Silo, the Yield Forwarder, the OFT adapter, the
      // distributor), so an ordinary wallet cannot receive iTRY, cannot send
      // it, and therefore cannot stake OR unstake. `maxDeposit` reads
      // uint.max throughout — the vault has no idea. Fork-proven 2026-08-14:
      // the deposit reverts for a fresh account and the whole cycle succeeds
      // the moment the role is granted (see the worker-api fixture
      // `witry-cooldown-cycle.test.ts`, which fails loudly if
      // `transferState` ever reaches 2 so this flag gets flipped back).
      //
      // NB the vault's own `maxRedeem`/`maxWithdraw` report the full balance
      // while the cooldown is set even though `withdraw`/`redeem` revert —
      // family-wide StakedUSDeV2 behaviour (sNUSD does it too), not a Brix
      // bug. Never size an exit here off the 4626 views.
      //
      // COOLDOWN: the docs say 3 days; the contract answered **300 seconds**
      // on 2026-08-14, an owner-settable dial with no timelock and a 90-day
      // ceiling (docs also still label staking "coming soon" while $8.7M is
      // staked). The pin below is the documented steady state and the
      // `erc4626-cooldown` reader overrides it with the live value every
      // fetch, so neither number can go stale on the row.
      //
      // Ethereum-only. MegaETH (4326) carries same-address-book LayerZero
      // OFTs for both tokens — 94 % of the wiTRY supply actually sits there —
      // but that contract is a bare ERC-20: `asset()`, `totalAssets()`,
      // `cooldownDuration()` and `convertToAssets()` all revert, and the
      // staking logic is Ethereum-canonical (MegaETH stakes round-trip
      // through LayerZero OVault). A second row would be a second vault that
      // does not exist.
      reader: 'erc4626-cooldown',
      address: '0xe346c29b5b60ef870b9724c57ccfbbc631e47dee',
      underlying: '0xb492b4afd9658093694cf9452d5c272e8230f3b0', // iTRY
      symbol: 'wiTRY',
      brand: 'Brix',
      // The backing is a regulated fund's shares held with Zodia Custody plus
      // an on-chain Fast Access Vault, marked by a licensed administrator's
      // daily NAV. Attested, not verifiable on-chain — and Brix's own
      // Accountable proof-of-reserves dashboard is documented as launching
      // "day 1 of official launch", i.e. not live at integration.
      solvency: 'nav-attested',
      description:
        "iTRY is a Turkish lira stablecoin backed by shares in a regulated basket of Turkish money-market funds, custodied off-chain and marked to a daily fund NAV. wiTRY stakes it: the NAV gain is minted as fresh iTRY weekly and vests into the share price over ~69 hours. NOT OPEN YET — iTRY transfers are whitelist-gated to the protocol's own contracts, so an ordinary wallet can neither hold nor stake it. The rate is earned in LIRA: Turkish paper pays this much because the lira depreciates, so a dollar-based holder keeps only what is left after the TRY/USD move.",
      decimals: 18,
      isRebasing: false,
      isMintable: false,
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 3 * 86400,
      yieldFetcher: brixFetcher,
      yieldKey: WITRY_KEY,
    },
    {
      // Cap stcUSD — a plain instant ERC-4626 over cUSD, Cap's stablecoin.
      // 98.1M cUSD held / 91.3M shares, price 1.0751 (2026-08-14). Yield is
      // what Cap's whitelisted operators pay to borrow the backing (56 % drawn
      // at integration) plus its reserve strategies.
      //
      // `lockDuration()` = 86400 is NOT a user lock — it is the window over
      // which a notified reward batch vests, the Ethena `VESTING_PERIOD` shape
      // under different names (`totalLocked` = unvested, `lastNotify` = the
      // batch stamp). Fork-verified 2026-08-14: deposit 1,000 cUSD and redeem
      // in the SAME transaction sequence returns 999.999999999999999999 cUSD —
      // instant, permissionless, no cooldown, no fee. `maxDeposit` is uint.max
      // and `maxRedeem` tracks the balance.
      //
      // No `solvency`: 56 % of the backing is lent to operators covered by
      // third-party restaker delegation rather than by borrower collateral,
      // and that mechanism is not assessed here. Absent is the honest answer —
      // the builder marks `coverage.pending.counterparty` rather than
      // defaulting to `overcollateralized`.
      //
      // Ethereum only. The same vanity address on MegaETH (4326, 25.2M — 21.6 %
      // of supply) and Katana (747474, 0.92M) is a bridged ERC-20 whose
      // `asset()` / `totalAssets()` / `convertToAssets()` all revert, and whose
      // `name()` differs by one character ("Staked Cap USD" vs mainnet's
      // "Staked cap USD") — the split that cost those chains their intrinsic
      // yield until `capFetcher` began emitting both group keys.
      address: '0x88887be419578051ff9f4eb6c858a951921d8888',
      underlying: '0xcccc62962d17b8914c62d74ffb843d73b2a3cccc', // cUSD
      symbol: 'stcUSD',
      brand: 'Cap',
      description:
        "cUSD is Cap's stablecoin, minted against stablecoin deposits. stcUSD stakes it with an appreciating share price: the yield is the interest Cap's whitelisted operators pay to borrow the backing, plus its reserve strategies, notified in batches that vest into the share price over 24 hours. Stake and unstake are instant, permissionless and free — a redeem in the same block as the deposit settles at par.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: capFetcher,
      yieldKey: STCUSD_KEY,
    },
    {
      // Wren wstGBP — a value-accruing wrapper over tGBP, the FCA-registered
      // GBP stablecoin issued by BCP Technologies (cash + short-dated gilts,
      // 1:1 redeemable, listed on Coinbase and Kraken). **The second non-USD
      // row on this surface after Brix's wiTRY** — see the lira note there:
      // `supplyRate` is asset-denominated, so a GBP rate belongs beside a
      // dollar one only with the currency stated, which is why it is in the
      // description and in `props.savings.base: 'GBP'`. The FX exposure is far
      // milder than the lira's, but it is the same axis.
      //
      // NOT ERC-4626 in any part — `asset()`, `totalAssets()`,
      // `convertToAssets()` and every `max*` view revert. The token prices
      // ITSELF through `navprice()`, a figure the issuer restates roughly
      // weekly in ~6.7 bps steps, hence the `wren-nav` reader.
      //
      // Fork-verified 2026-08-14 on the live contract:
      //  - `mint(uint256)` is PERMISSIONLESS and settles at NAV with no entry
      //    fee (`mintcost() == navprice()`): 100 tGBP → 99.1355 wstGBP from a
      //    fresh address. tGBP itself transfers freely (the iTRY check).
      //  - `redeem(uint256)` is the instant exit and pays `burncost()`, which
      //    is ~25 bps BELOW the NAV — 10 wstGBP returned exactly
      //    10.061984858035600520 tGBP. That gap is reported as the signed
      //    `redemptionDiscountBps`, not smoothed into the share price.
      //  - **`smelt(uint256)` burns shares and pays NOTHING immediately**,
      //    booking a record under `redemptions(i)` for later settlement. It is
      //    a distinct, uncharacterised path — never route an exit to it.
      //    `exit(uint256)` reverts `0x9b604d0b` for an ordinary caller.
      //
      // Small: ~83.3k wstGBP over ~84.0k tGBP (≈ $114k at 1.352 GBP/USD).
      // Ethereum only — no other chain carries the contract, though tGBP
      // itself is a LayerZero OFT on six.
      reader: 'wren-nav',
      address: '0x57c3571f10767e49c9d7b60feb6c67804783b7ae',
      underlying: '0x27f6c8289550fce67f6b50bed1f519966afe5287', // tGBP
      symbol: 'wstGBP',
      brand: 'Wren',
      // The backing is an FCA-registered issuer's segregated cash and gilt
      // reserves, and the share price is a NAV the issuer restates — attested,
      // not verifiable on-chain. Same class as Apyx and Brix.
      solvency: 'nav-attested',
      description:
        'tGBP is a pound-sterling stablecoin from BCP Technologies, an FCA-registered issuer, backed 1:1 by cash and short-dated UK gilts held in segregated accounts. wstGBP wraps it and accrues the gilt yield through a NAV the issuer restates weekly — so the rate is earned in POUNDS, and a dollar-based holder keeps only what is left after the GBP/USD move. Minting is permissionless and free; redeeming is instant but settles ~25 bps below NAV.',
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: wrenFetcher,
      yieldKey: WSTGBP_KEY,
    },
    {
      // Apyx apyUSD — ERC-4626 over apxUSD, the "Dividend-Backed
      // Dollar" (variable-rate perpetual preferred stock of DAT
      // companies, STRC/Strategy). Yield = off-chain dividends minted
      // as apxUSD and dripped in through a 14-day LinearVestV0
      // (0x0d62b4cc…, `pullVestedYield`). No on-chain solvency
      // invariant — RWA/NAV trust class. apxUSD minting is
      // EIP-712/m-of-n gated (institutional) but the 4626 stake side is
      // permissionless (`maxDeposit` = uint.max), with deep secondary
      // apxUSD liquidity (Curve / Uni v4).
      //
      // EXIT (fork-verified 2026-08-04, and NOT what the docs site
      // describes): a 4626 redeem pays the caller nothing. It takes the
      // vault's flat `unlockingFee()` (0.1%), escrows the apxUSD in the
      // unlock-RECEIPT contract (`receipt()` = 0x9bf51f33…) and mints a
      // soulbound ERC-721 keyed by tokenId. Claims are blocked for
      // `feeCurve.minDuration` (3 days — hence the cooldown below) and
      // then carry a fee decaying to zero at `maxDuration` (20 days):
      // measured 3.40% at 3d, 2.60% at 7d, 0% at 20d. The curve is
      // GLOBAL and admin-settable (capped 5%), so it re-prices receipts
      // already in flight — quote `previewClaim(tokenId)` live rather
      // than trusting a stored figure. The vault's legacy
      // `unlockToken()` (0x93775e2d…) is still deployed but off the
      // redemption path. Base/BNB carry bridged mirrors whose 4626
      // surface reverts (plus Solana) — Ethereum-only row.
      address: '0x38eeb52f0771140d10c4e9a9a72349a329fe8a6a',
      underlying: '0x98a878b1cd98131b271883b390f68d2c90674665', // apxUSD
      symbol: 'apyUSD',
      solvency: 'nav-attested',
      brand: 'Apyx',
      description:
        "apxUSD is Apyx's dividend-backed dollar — collateralized by variable-rate perpetual preferred stock issued by Digital Asset Treasury companies (e.g. Strategy's STRC). apyUSD stakes it and accrues the preferred-share dividends via an appreciating share price. Exiting mints a transfer-locked receipt NFT: it cannot be claimed for 3 days, then costs a fee that decays from ~3.4% to zero at 20 days (plus a flat 0.1% on the way out). The backing lives off-chain (NAV trust).",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      // Earliest claim = the receipt's `claimableAt` (`feeCurve.minDuration`,
      // 3 days). Waiting the full 20 days is what makes the exit free —
      // see the note above; both bounds are admin-mutable.
      withdrawalCooldownSeconds: 3 * 86400,
      withdrawQueue: '0x9bf51f33955ec70f87c4b5c49441815589043237',
      yieldFetcher: apyxFetcher,
      yieldKey: APYUSD_KEY,
    },
    // ---- Saturn (saturn.credit) — USDat / sUSDat ----------------------
    //
    // `USDat` is an M^0 extension token (a wrapped `$M` claim on tokenized
    // Treasuries) and `sUSDat` is the vault that stakes it into **Strategy's
    // STRC**, the variable-rate perpetual preferred stock, passing the
    // dividend through. Ethereum only: the same addresses on BNB (56) and
    // Monad (143) are bare LayerZero OFT mirrors whose entire 4626 surface
    // reverts, so staking round-trips to mainnet (the Cap / Apyx / Brix
    // precedent) — Monad nevertheless holds $31.1M of USDat.
    //
    // Registered `volatile`, and the reason is the whole assessment.
    // `totalAssets = usdatBalance + (strcBalance − unvested) × STRC price`,
    // and 96.5 % of NAV is STRC held outright — 723,435.68 STRC against just
    // $2.55M of actual USDat, with the implied $97.8893 matching Chainlink
    // STRC/USD to 0.01 %. So the solvency invariant is genuinely on-chain and
    // checkable (better than Spark V2 / Maple / Native, none of which can be),
    // and the share price nonetheless moves with a preferred stock. The income
    // leg is real and clockwork — 50 `transferInRewards` distributions over
    // 152.3 days at a 3.11-day cadence, 12.19 % APR, which is what DefiLlama's
    // 15.58 % and Saturn's "11 %+" both measure — while the vault's whole life
    // returned **+3.73 % with a −22.82 % max drawdown**. The rate published
    // here is the REALIZED one; see `saturnFetcher` for why there is no
    // DefiLlama fallback.
    //
    // NOT `secondaryMarketOnly`, and the fork test is why. USDat's own mint is
    // whitelist-gated (`isWhitelistEnabled()` true, `isAllowedToWrap(<random
    // EOA>)` false) — but that is a fact about the UNDERLYING, one level below
    // this row. STAKING is permissionless: `maxDeposit` is `uint256.max`,
    // `_deposit` consults no allowlist, and a fork deposit from an
    // unwhitelisted account mints at par. Marking the vault
    // `secondaryMarketOnly` made `/v1/actions/vaults/deposit` refuse a mint
    // that works, which is the Brix trap inverted — there a `maxDeposit` of
    // `uint.max` hid a gate, here a gate on a neighbouring contract hid an
    // open door. Acquiring the USDat is an ordinary swap (the Curve pool
    // round-trips 100k at −3 bps with USDat at $0.9997), i.e. a routing
    // question, not a property of this vault.
    //
    // Exit is `request-based` and there is exactly one protocol route:
    // `requestRedeem(shares, minUsdat)` escrows the shares and mints an
    // ERC-721 receipt, a `PROCESSOR_ROLE` batch sells STRC, then `claim`.
    // Measured latency ~24-25 h (Saturn documents 3-7 days) — pinned at 24 h
    // here and NOT read on-chain, because nothing on the queue publishes it:
    // it is an operator cadence, not a dial. Verified on-chain 2026-09-10.
    {
      reader: 'saturn-vault',
      address: '0xd166337499e176bbc38a1fbd113ab144e5bd2df7',
      underlying: '0x23238f20b894f29041f48d88ee91131c395aaa71', // USDat
      symbol: 'sUSDat',
      brand: 'Saturn',
      solvency: 'overcollateralized',
      description:
        "Saturn's staking vault: deposited USDat (a dollar backed by tokenized Treasuries) buys Strategy's STRC preferred stock, whose dividend is paid in every three days. Almost all the backing is that one stock, marked to market — so the share price falls when STRC does, and it has: a ~12% dividend over the first five months still returned only 3.7% annualised, after a 22.8% drawdown. Leaving is a request a Saturn operator settles in about a day by selling STRC, paying what that sale realises rather than the share price on the day you asked.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 86_400,
      withdrawQueue: '0x4bc9fec04f0f95e9b42a3ef18f3c96fb57923d2e',
      yieldFetcher: saturnFetcher,
      yieldKey: SUSDAT_KEY,
    },
    // ---- Strata (docs.strata.markets) — senior/junior risk tranches ----
    //
    // Six CDO markets, twelve ERC-4626 tranche tokens, all Ethereum.
    // Each market pools a yield-bearing collateral (sUSDe, sNUSD,
    // mHYPER, mM1-USD, sUSDat, PRIME) and splits its realized yield:
    // the senior earns a benchmark-floored rate (its `maxDeposit` is
    // coverage-cap-gated), the junior takes the residual — levered
    // upside AND first-loss capital (jrUSDat marked down to ~0.36
    // absorbing the 2026-06/07 sUSDat drawdown — the SATURN VAULT fell
    // 22.8 %, USDat itself never lost its peg; junior redemptions pause when
    // senior coverage drops below the market's minimum). Exits are
    // two-legged and both escrow through the market's cooldown
    // contracts, claimed via `finalize`: redeeming into the collateral
    // token runs a governance cooldown (0 seconds = effectively instant
    // on every market at integration), while the plain-4626 base-asset
    // redeem unstakes the collateral first (7d Ethena, 10d Neutrl, ≲3d
    // Midas) — hence `request-based`, with `withdrawQueue` pointing at
    // the market's UnstakeCooldown. `previewRedeem` (but NOT
    // `convertToAssets`) already nets the coverage-dependent exit fee
    // (0–20 bps). Rates are Strata's own trailing-7d realized figures —
    // see the fetcher for the junior-tranche caveats. Verified on-chain
    // 2026-08-04.
    {
      address: '0x3d7d6fdf07ee548b939a80edbc9b2256d0cdc003',
      underlying: '0x4c9edd5852cd905f086c759e8383e09bff1e68b3', // USDe
      symbol: 'srUSDe',
      solvency: 'tranched-senior',
      brand: 'Strata',
      description:
        "Strata's senior tranche over Ethena USDe: pooled deposits sit in sUSDe and the senior earns a benchmark-floored share of its yield, with the junior tranche absorbing losses first. Exits claim through cooldown contracts — the sUSDe leg is effectively instant, the USDe leg waits Ethena's 7-day unstake — minus a small coverage-dependent exit fee.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 7 * 86400,
      withdrawQueue: '0x735eddf50ca2371aa48466469c742e684c610f74',
      yieldFetcher: strataFetcher,
      yieldKey: SRUSDE_KEY,
    },
    {
      address: '0xc58d044404d8b14e953c115e67823784dea53d8f',
      underlying: '0x4c9edd5852cd905f086c759e8383e09bff1e68b3', // USDe
      symbol: 'jrUSDe',
      solvency: 'tranched-junior',
      brand: 'Strata',
      description:
        "Strata's junior tranche over Ethena USDe: earns the residual sUSDe yield after the senior's benchmark payment — levered upside when rates run hot, first-loss capital when they don't (the trailing APR can go negative). Exits mirror srUSDe (instant-ish sUSDe leg, 7-day USDe leg) but pause while senior coverage is below 105%.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 7 * 86400,
      withdrawQueue: '0x735eddf50ca2371aa48466469c742e684c610f74',
      yieldFetcher: strataFetcher,
      yieldKey: JRUSDE_KEY,
    },
    {
      address: '0x65a44528e8868166401ea08b549e19552af589db',
      underlying: '0xe556aba6fe6036275ec1f87eda296be72c811bce', // NUSD
      symbol: 'srNUSD',
      solvency: 'tranched-senior',
      brand: 'Strata',
      description:
        "Strata's senior tranche over Neutrl NUSD: pooled deposits sit in sNUSD and the senior earns a benchmark-floored share of its yield, junior-protected. Exits claim through cooldown contracts — the sNUSD leg is effectively instant, the NUSD leg waits Neutrl's 10-day unstake — minus a 0–5 bps coverage-dependent exit fee.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 10 * 86400,
      withdrawQueue: '0x2a52363a2a0d765b31cb117a8e4d9ce58c2bc749',
      yieldFetcher: strataFetcher,
      yieldKey: SRNUSD_KEY,
    },
    {
      address: '0xfc807058a352b61aeef6a38e2d0fc3990225e772',
      underlying: '0xe556aba6fe6036275ec1f87eda296be72c811bce', // NUSD
      symbol: 'jrNUSD',
      solvency: 'tranched-junior',
      brand: 'Strata',
      description:
        "Strata's junior tranche over Neutrl NUSD: residual yield after the senior's benchmark payment, first-loss against sNUSD underperformance. Exits run the same cooldown claims as srNUSD (NUSD leg = 10-day unstake) with a 0–20 bps coverage-dependent fee, and pause while senior coverage is below the market minimum.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 10 * 86400,
      withdrawQueue: '0x2a52363a2a0d765b31cb117a8e4d9ce58c2bc749',
      yieldFetcher: strataFetcher,
      yieldKey: JRNUSD_KEY,
    },
    {
      // 18-dec shares over 6-dec USDC (the yUSD case) — the collateral
      // is 18-dec mHYPER, but the vault accounts in USDC.
      address: '0x627ea69929212916ec57b1b26d2e1a19f6129b53',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'srmHYPER',
      solvency: 'tranched-senior',
      brand: 'Strata',
      description:
        "Strata's senior tranche over Midas mHYPER (Hyperithm's multi-chain stablecoin yield strategy), accounted in USDC: benchmark-floored yield (Aave USDC/USDT supply rate + 3% premium), junior-protected. Exits claim through cooldown contracts — the mHYPER leg is effectively instant, the USDC leg follows Midas's ≲3-day redemption schedule — minus a 0–5 bps fee.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 3 * 86400,
      withdrawQueue: '0x7910cb19a4f5a36caa2bed0af82cbfff2e08805c',
      yieldFetcher: strataFetcher,
      yieldKey: SRMHYPER_KEY,
    },
    {
      address: '0xeb205d26e9e605ec82d1c0d652e00037c278714b',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'jrmHYPER',
      solvency: 'tranched-junior',
      brand: 'Strata',
      description:
        "Strata's junior tranche over Midas mHYPER, accounted in USDC: residual yield after the senior's benchmark payment, first-loss against the mHYPER strategy. Exits mirror srmHYPER (USDC leg ≲3 days via Midas) and pause while senior coverage is below the market minimum.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 3 * 86400,
      withdrawQueue: '0x7910cb19a4f5a36caa2bed0af82cbfff2e08805c',
      yieldFetcher: strataFetcher,
      yieldKey: JRMHYPER_KEY,
    },
    {
      address: '0xcced21d609cac4a272d0c01a8ff4de9cebc40d60',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'srmM1-USD',
      solvency: 'tranched-senior',
      brand: 'Strata',
      description:
        "Strata's senior tranche over Midas mM1-USD, accounted in USDC: benchmark-floored share of the mM1-USD yield, junior-protected. Exits claim through cooldown contracts — the mM1-USD leg is effectively instant, the USDC leg follows Midas's redemption schedule.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 3 * 86400,
      withdrawQueue: '0x6f6904ed406cefb34893054a742c6148e9d681c1',
      yieldFetcher: strataFetcher,
      yieldKey: SRMM1USD_KEY,
    },
    {
      address: '0xf7eb8dfec75c42d2d2247fe76ccaedc59f821688',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'jrmM1-USD',
      solvency: 'tranched-junior',
      brand: 'Strata',
      description:
        "Strata's junior tranche over Midas mM1-USD, accounted in USDC: residual yield after the senior's benchmark payment, first-loss against the mM1-USD strategy. Exits mirror srmM1-USD and pause while senior coverage is below the market minimum.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 3 * 86400,
      withdrawQueue: '0x6f6904ed406cefb34893054a742c6148e9d681c1',
      yieldFetcher: strataFetcher,
      yieldKey: JRMM1USD_KEY,
    },
    {
      // Accounted in 6-dec USDat (Saturn Dollar); collateral is staked
      // 18-dec sUSDat.
      address: '0xfaa9a0e1db9e22ae3a20b2b58a68dc24d053d066',
      underlying: '0x23238f20b894f29041f48d88ee91131c395aaa71', // USDat
      symbol: 'srUSDat',
      solvency: 'tranched-senior',
      brand: 'Strata',
      description:
        "Strata's senior tranche over Saturn USDat (a Strategy-preferred-stock-backed dollar): fixed yield at 65% of the STRC dividend rate, junior-protected — the senior held its rate straight through the 2026-06/07 sUSDat drawdown that the junior absorbed. Exits claim through cooldown contracts (the sUSDat leg effectively instant) minus a 0–5 bps fee.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawQueue: '0x616af703d5739f2122af48c0d1ce0b37e09381ac',
      yieldFetcher: strataFetcher,
      yieldKey: SRUSDAT_KEY,
    },
    {
      address: '0x011e55d2b28306458e37ca7e997c879bb25a455d',
      underlying: '0x23238f20b894f29041f48d88ee91131c395aaa71', // USDat
      symbol: 'jrUSDat',
      solvency: 'tranched-junior',
      brand: 'Strata',
      description:
        "Strata's junior tranche over Saturn USDat — first-loss capital that proved it: the 2026-06/07 drawdown in the collateral — Saturn's sUSDat vault, which is marked to Strategy's STRC preferred stock and fell 22.8%; USDat itself held its peg — marked jrUSDat down to ~0.36 while the senior stayed whole, and its trailing APR is recovery-inflated. Exits claim through cooldown contracts and pause while senior coverage is below the market minimum.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawQueue: '0x616af703d5739f2122af48c0d1ce0b37e09381ac',
      yieldFetcher: strataFetcher,
      yieldKey: JRUSDAT_KEY,
    },
    {
      address: '0x35bff778d3fc53a561486bf28e761428499232eb',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'srPRIME',
      solvency: 'tranched-senior',
      brand: 'Strata',
      description:
        "Strata's senior tranche over Hastra PRIME (yield sourced from Figure's YLDS-based lending), accounted in USDC: benchmark-floored share of the PRIME yield, junior-protected. Exits claim through cooldown contracts — the PRIME leg is effectively instant, the USDC leg follows the strategy's redemption schedule.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawQueue: '0x440938fcb03d64afe50a8168d4b3cfcc341bfbaa',
      yieldFetcher: strataFetcher,
      yieldKey: SRPRIME_KEY,
    },
    {
      address: '0xf4c91f24e20ee8ed5eda905e501a1136334c2f27',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'jrPRIME',
      solvency: 'tranched-junior',
      brand: 'Strata',
      description:
        "Strata's junior tranche over Hastra PRIME, accounted in USDC: residual yield after the senior's benchmark payment, first-loss against the PRIME strategy. Exits mirror srPRIME and pause while senior coverage is below the market minimum.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawQueue: '0x440938fcb03d64afe50a8168d4b3cfcc341bfbaa',
      yieldFetcher: strataFetcher,
      yieldKey: JRPRIME_KEY,
    },
    {
      // Avant savETH — staked avETH (Ethereum, where avETH is native);
      // StakedUSDeV2-style 24h cooldown.
      address: '0xda06ee2dacf9245aa80072a4407debdea0d7e341',
      underlying: '0x9469470c9878bf3d6d0604831d9a3a366156f7ee', // avETH
      symbol: 'savETH',
      brand: 'Avant',
      description:
        "avETH is Avant's ETH-denominated synthetic asset; savETH stakes it and accrues yield from Avant's market-neutral trading strategies via an appreciating share price, with a 24-hour unstake cooldown.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 86_400,
      yieldFetcher: savethFetcher,
      yieldKey: 'Staked avETH::savETH',
    },
    {
      address: '0xa3931d71877c0e7a3148cb7eb4463524fec27fbd',
      underlying: '0xdc035d45d973e3ec169d2276ddab16f1e407384f',
      symbol: 'sUSDS',
      brand: 'Sky',
      description:
        "USDS is Sky's (ex-MakerDAO) upgraded DAI; sUSDS accrues the Sky Savings Rate, funded by the protocol's collateralised lending and RWA revenue. Instant permissionless entry and exit.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: susdsFetcher,
      yieldKey: 'sUSDS::SUSDS',
    },
    {
      address: '0x99cd4ec3f88a45940936f469e4bb72a2a701eeb9',
      underlying: '0xdc035d45d973e3ec169d2276ddab16f1e407384f',
      symbol: 'stUSDS',
      brand: 'Sky',
      description:
        "Sky's higher-yield staking module over USDS, with a utilisation-driven rate instead of the flat Sky Savings Rate. Withdrawals are instant but liquidity-dependent — the pool targets ~90% utilisation, so maxWithdraw can temporarily return less than your balance.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: susdsFetcher,
      yieldKey: 'Staked USDS::stUSDS',
    },
    {
      // Frax sfrxUSD — `LinearRewardsErc4626` over frxUSD. Plain
      // instant 4626 exit; the rate is set by the vault's
      // `maxDistributionPerSecondPerAsset` cap rather than by
      // utilisation (see the frax fetcher for the exact maths).
      address: '0xcf62f905562626cfcdd2261162a51fd02fc9c5b6',
      underlying: '0xcacd6fd266af91b8aed52accc382b4e165586e29', // frxUSD
      symbol: 'sfrxUSD',
      brand: 'Frax',
      description:
        "frxUSD is Frax's fiat-redeemable USD stablecoin; sfrxUSD accrues protocol revenue streamed to the vault, with the rate set by a distribution cap rather than utilisation (in practice the cap is the rate). Instant exit.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: fraxSavingsFetcher,
      yieldKey: 'SFRXUSD',
    },
    {
      // Resupply sreUSD — the savings leg of the reUSD CDP, and the same
      // `LinearRewardsErc4626` family as the two Frax rows below (so it
      // shares `linearRewards.ts`). Plain instant 4626: `maxDeposit =
      // uint.max`, no cooldown, no exit fee (verified on-chain 2026-08).
      //
      // The rate is deliberately modest and that is not an error: sreUSD is
      // funded by Resupply's revenue share plus the DYNAMIC OFF-PEG component
      // of the borrow rate, not by the whole borrow rate — 0.74% APR against
      // a 2.27% borrow rate at integration, cross-checked against DefiLlama
      // (0.711%) and the protocol's own `Utilities.sreusdRates()` lens.
      address: '0x557ab1e003951a73c12d16f0fea8490e39c33c35',
      underlying: '0x57ab1e0003f623289cd798b1824be09a793e4bec', // reUSD
      symbol: 'sreUSD',
      brand: 'Resupply',
      description:
        'reUSD is Resupply\u2019s CDP stablecoin, minted against other lenders\u2019 supply positions (Curve Lend / Fraxlend). sreUSD streams the protocol\u2019s revenue share plus the off-peg borrow-rate amplifier to depositors on a 7-day linear cycle. Instant exit.',
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: sreusdSavingsFetcher,
      yieldKey: 'SREUSD',
    },
    {
      // f(x) Protocol fxSAVE — 4626 auto-compounder over `fxSP`, the f(x)
      // Stability Pool share (it holds 57.4M of fxSP's 59.4M supply, i.e.
      // ~97% of the pool). Plain instant exit: `maxDeposit = uint.max`,
      // `maxRedeem == balanceOf` on a live holder, `previewRedeem ==
      // convertToAssets` (no fee, no cooldown) — verified on-chain 2026-08-09.
      //
      // The underlying is fxSP and NOT fxUSD: fxSP appreciates on its own
      // ($1.0101 vs fxUSD's $1.0000 at integration), so pricing `totalAssets`
      // off fxUSD would understate TVL by ~1%. The token-list row's
      // `props.savings.underlying: 'fxUSD'` is the classifier's economic
      // underlying, a different question from `asset()`.
      address: '0x7743e50f534a7f9f1791dde7dcd89f7783eefc39',
      underlying: '0x65c9a641afceb9c0e6034e558a319488fa0fa3be', // fxSP
      symbol: 'fxSAVE',
      brand: 'f(x) Protocol',
      description:
        "fxUSD is f(x) Protocol's stablecoin; its Stability Pool (fxSP) backstops leveraged positions and earns the protocol's rebalance and liquidation revenue. fxSAVE auto-compounds that revenue back into fxSP, so the share price appreciates. Instant permissionless exit, no fee or cooldown.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: fxSaveFetcher,
      yieldKey: FXSAVE_KEY,
    },
    {
      // Frax sFRAX — the pre-rename sibling of sfrxUSD, over legacy
      // FRAX. Still live and roughly 2x sfrxUSD's size, so it stays a
      // first-class row rather than an alias. Same contract family, so
      // it shares `fraxSavingsFetcher` (one multicall covers both).
      address: '0xa663b02cf0a4b149d2ad41910cb81e23e1c41c32',
      underlying: '0x853d955acef822db058eb8505911ed77f175b99e', // FRAX
      symbol: 'sFRAX',
      brand: 'Frax',
      description:
        'The pre-rename sibling of sfrxUSD over legacy FRAX — same vault family, same cap-set rate mechanism. Instant exit.',
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: fraxSavingsFetcher,
      yieldKey: 'SFRAX',
    },
    {
      // Inverse sDOLA — 4626 auto-compounder over the DOLA Savings
      // Account; yield is FiRM's DBR revenue via a continuous on-chain
      // auction, no rehypothecation. Instant permissionless exit
      // (maxDeposit = uint.max verified 2026-08; ~6.9M DOLA TVL).
      // Ethereum-only; Inverse propagates the rate cross-chain via
      // CCIP, so bridged deployments may appear later. The jDOLA
      // junior tranche (48h exit window + fee, ~$28k) is deliberately
      // not registered.
      address: '0xb45ad160634c528cc3d2926d9807104fa3157305',
      underlying: '0x865377367054516e17014ccded1e7d814edc9ce4', // DOLA
      symbol: 'sDOLA',
      brand: 'Inverse',
      description:
        "DOLA is Inverse Finance's stablecoin; sDOLA compounds FiRM's DBR interest revenue, converted to DOLA by a continuous on-chain auction — deposits never leave the contract (no rehypothecation). Instant permissionless exit.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: sdolaFetcher,
      yieldKey: SDOLA_GROUP_KEY,
    },
    {
      address: '0x83f20f44975d03b1b09e64809b757c47f942beea',
      underlying: '0x6b175474e89094c44da98b954eedeac495271d0f',
      symbol: 'sDAI',
      brand: 'Maker',
      description:
        "DAI deposited into MakerDAO's Dai Savings Rate; the share price accrues the DSR. Instant entry and exit through the Pot.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      // No dedicated sDAI APR fetcher today — supplyRate falls to 0.
    },
    {
      address: '0xd3fd63209fa2d55b07a0f6db36c2f43900be3094',
      underlying: '0x09d4214c03d01f49544c0448dbe3a27f768f2b34',
      symbol: 'wsrUSD',
      brand: 'Reservoir',
      // NB: NOT a wrapper of srUSD despite the name — verified on-chain
      // 2026-08-19 (see RESERVOIR.md): a second-generation Savingcoin
      // directly over rUSD with its own governance-set accumulator.
      // Deposits burn rUSD and exits mint it, so the vault custodies
      // nothing and there is no on-chain solvency invariant (the
      // CreditEnforcer's covenant minimums are zeroed and its adapters
      // removed). Exit to rUSD is instant/unlimited; par exit to USDC is
      // bounded by live USDC-PSM inventory (~0.1% of the book).
      description:
        "rUSD is Reservoir's asset-backed stablecoin; wsrUSD accrues the Reservoir savings rate (governance-set, funded by the protocol's RWA and credit portfolios) directly on rUSD — reserves are managed off-chain with no on-chain solvency invariant. Exit to rUSD is instant; converting onward to USDC at par depends on the PSM's live inventory, a small fraction of the book.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: srusdFetcher,
      yieldKey: 'Wrapped Savings rUSD::WSRUSD',
    },
    {
      address: '0x1202f5c7b4b9e47a1a484e8b270be34dbbc75055',
      // `asset()` is USR itself: wstUSR is a 4626 over USR (deposit USR, get
      // wstUSR) that holds the rebasing stUSR internally. The row carried
      // stUSR `0x6c8984bc…` as its underlying until 2026-09-18, which would
      // have had the generic deposit route pull a token the vault never asks
      // for.
      underlying: '0x66a1e37c9b0eaddca17d3662d6c05f4decf3e110',
      symbol: 'wstUSR',
      brand: 'Resolv',
      description:
        "USR is Resolv's delta-neutral synthetic dollar (ETH collateral hedged with perpetual shorts; the RLP insurance layer absorbs losses first). stUSR stakes it and rebases; wstUSR is the non-rebasing wrapper. Instant unwrap with no cooldown.",
      decimals: 18,
      // Wraps the rebasing stUSR — wstUSR itself does NOT rebase.
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: wstusrFetcher,
      yieldKey: 'Resolv wstUSR::WSTUSR',
    },
    {
      address: '0xc8cf6d7991f15525488b2a83df53468d682ba4b0',
      underlying: '0xfa2b947eec368f42195f24f36d2af29f7c24cec2',
      symbol: 'sUSDf',
      brand: 'Falcon',
      description:
        "USDf is Falcon Finance's overcollateralised synthetic dollar; sUSDf stakes it and accrues yield from Falcon's diversified institutional trading strategies. The share exits instantly to USDf — the 7-day cooling period applies only to the downstream USDf → collateral redemption.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      // sUSDf → USDf is instant; the 7-day cooling applies to the
      // downstream USDf → collateral redemption (separate contract).
      // See TIMELOCKS_AND_CHAINS.md.
      withdrawalMode: 'instant',
      yieldFetcher: susdfFetcher,
      yieldKey: 'Staked Falcon USD::sUSDf',
    },
    {
      address: '0xdbdc1ef57537e34680b898e1febd3d68c7389bcb',
      underlying: '0x48f9e38f3070ad8945dfeae3fa70987722e3d89c',
      symbol: 'siUSD',
      brand: 'InfiniFi',
      description:
        "iUSD is infiniFi's USD receipt token over USDC; siUSD accrues a blended rate from the protocol's laddered allocation across liquid and locked yield instruments. Instant exit while reserves cover it — the locked tier (liUSD) is a separate token.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: infinifiFetcher,
      yieldKey: 'Staked infiniFi USD::siUSD',
    },
    {
      address: '0x80ac24aa929eaf5013f6436cda2a7ba190f5cc0b',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
      symbol: 'syrupUSDC',
      solvency: 'undercollateralized',
      brand: 'Maple',
      description:
        "Deposits fund Maple's institutional credit book — collateralised loans to vetted trading firms — and yield is loan interest. The share price is managed by Maple with no on-chain solvency invariant; exits go through a cycle-based withdrawal queue (~7 days typical), though most process much faster via the instant buffer.",
      decimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 604_800,
      yieldFetcher: mapleFetcher,
      yieldKey: 'SYRUPUSDC',
    },
    {
      address: '0x356b8d89c1e1239cbbb9de4815c39a1474d5ba7d',
      underlying: '0xdac17f958d2ee523a2206206994597c13d831ec7',
      symbol: 'syrupUSDT',
      solvency: 'undercollateralized',
      brand: 'Maple',
      description:
        'The USDT sibling of syrupUSDC — same Maple institutional credit book, same cycle-based withdrawal queue.',
      decimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 604_800,
      yieldFetcher: mapleFetcher,
      yieldKey: 'SYRUPUSDT',
    },
    {
      address: '0x87b65c4aaffa76881f9e96f3e7ed945ddfc3cd7a',
      underlying: '0xe343167631d89b6ffc58b88d6b7fb0228795491d', // USDG
      symbol: 'syrupUSDG',
      solvency: 'undercollateralized',
      brand: 'Maple',
      description:
        'The USDG sibling of syrupUSDC — same Maple institutional credit book, same cycle-based withdrawal queue.',
      decimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 604_800,
      yieldFetcher: mapleFetcher,
      yieldKey: 'SYRUPUSDG',
    },
    {
      // Hastra Democratized PRIME — ERC-4626 over wYLDS (itself ERC-4626 over
      // USDC), so the share/asset ratio accrues on-chain (`convertToAssets`).
      // Solana-primary; the Ethereum deployment exists (verified on-chain:
      // symbol PRIME, 6 decimals, asset() = wYLDS) but carries no user holders
      // yet. Deposits are whitelist-gated, so `isMintable` is false — we list
      // + price it, but don't advertise a permissionless mint that would
      // revert. Two-phase exit (`requestRedeem` → `completeRedeem`). APR from
      // the public Proof-of-Reserve feed via `hastraPrimeFetcher`.
      address: '0x19ebb35279a16207ec4ba82799cc64715065f7f6',
      underlying: '0x6ad038ca6c04e885630851278ca0a856ad9a66cc', // wYLDS
      symbol: 'PRIME',
      solvency: 'nav-attested',
      brand: 'Hastra',
      description:
        "Hastra's Democratized PRIME: an ERC-4626 over wYLDS (itself a vault over USDC), with the rate published via Hastra's Proof-of-Reserve feed. Deposits are whitelist-gated; exit is a two-phase request-then-complete redemption.",
      decimals: 6,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: false,
      withdrawalMode: 'request-based',
      yieldFetcher: hastraPrimeFetcher,
      yieldKey: 'Hastra PRIME::PRIME',
    },
    {
      // 3Jane USD3 — senior tranche of the credit-based money market
      // (docs.3jane.xyz): USDC funds unsecured fintech/crypto credit
      // lines through a MorphoCredit (Morpho Blue augmentation) market,
      // idle cash in Aave. Yearn-v3-strategy ERC-4626, 6-dec shares over
      // USDC, price accrues on-chain (`pricePerShare`). Exit is instant
      // but capped by real multi-source liquidity — idle USDC + what the
      // MorphoCredit market can redeem out of waEthUSDC — read via
      // `availableWithdrawLimit` (~4.7 % of the $75M TVL at 2026-08-04
      // integration), hence the `erc4626-withdraw-limit` reader. No
      // on-chain solvency invariant on the credit sleeves (Maple /
      // Spark-V2 trust class); unrealized losses zero the limit until
      // marked down. `isMintable: true` is structural — the whitelist is
      // disabled — but deposits are cap-gated (`USD3_SUPPLY_CAP`,
      // guarded-launch style) and the cap was FULL at integration time
      // (`availableDepositLimit = 0`), plus a 1,000-USDC minimum first
      // deposit; consult `maxDeposit` live before advertising the mint.
      reader: 'erc4626-withdraw-limit',
      address: '0x056b269eb1f75477a8666ae8c7fe01b64dd55ecc',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'USD3',
      solvency: 'tranched-senior',
      brand: '3Jane',
      description:
        "3Jane's senior tranche: USDC funds unsecured, credit-underwritten fintech and crypto credit lines through a MorphoCredit market, with idle cash parked in Aave. Yield is loan interest and the junior sUSD3 absorbs defaults first; exits are instant but capped by live liquidity, and deposits are supply-cap-gated.",
      decimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant-capped',
      yieldFetcher: threeJaneFetcher,
      yieldKey: USD3_KEY,
    },
    {
      // 3Jane sUSD3 — the junior, first-loss tranche: an ERC-4626 whose
      // underlying is USD3 itself (the reUSDe pattern — key the pair,
      // not the brand). Earns a fixed `TRANCHE_SHARE_VARIANT` slice of
      // pool interest over a much smaller base (~2x USD3's rate),
      // absorbs losses first, and its supply is capped by the
      // subordination ratio (deposits permissionless within it). Exit is
      // `startCooldown(shares)` → wait → `redeem` inside a withdrawal
      // window (+ a ProtocolConfig lock on new deposits, 0 at
      // integration time), so `request-based` with the cooldown pinned
      // from the live `cooldownDuration()` read (30 days, 2026-08-04;
      // governance-mutable via ProtocolConfig — the window read
      // ~100 years, i.e. effectively no expiry today).
      address: '0xf689555121e529ff0463e191f9bd9d1e496164a7',
      underlying: '0x056b269eb1f75477a8666ae8c7fe01b64dd55ecc', // USD3
      symbol: 'sUSD3',
      solvency: 'tranched-junior',
      brand: '3Jane',
      description:
        "3Jane's junior, first-loss tranche: an ERC-4626 over USD3 that earns a fixed share of pool interest on a smaller base (roughly 2x USD3's rate) and absorbs credit losses first. Exit requires a 30-day cooldown, then redeeming within the withdrawal window.",
      decimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 30 * 86_400,
      yieldFetcher: threeJaneFetcher,
      yieldKey: SUSD3_KEY,
    },
    {
      // Spark Vaults V2 — the mainnet book, and by far the largest of
      // the family. Only the Ethereum rows carry the intents contract as
      // `withdrawQueue`; the other chains have no deployment of it.
      ...sparkV2Base('spUSDC', 6),
      address: '0x28b3a8fb53b741a8fd78c0fb9a6b2393d896a43d',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      withdrawQueue: SPARK_V2_WITHDRAW_INTENTS,
      yieldKey: sparkVaultV2Key('1', 'spUSDC'),
    },
    {
      ...sparkV2Base('spUSDT', 6),
      address: '0xe2e7a17dff93280dec073c995595155283e3c372',
      underlying: '0xdac17f958d2ee523a2206206994597c13d831ec7', // USDT
      withdrawQueue: SPARK_V2_WITHDRAW_INTENTS,
      yieldKey: sparkVaultV2Key('1', 'spUSDT'),
    },
    {
      // The only non-stable in the family — underlying is WETH, so
      // share and underlying are both 18-decimal.
      ...sparkV2Base('spETH', 18),
      address: '0xfe6eb3b609a7c8352a241f7f3a21cea4e9209b8f',
      underlying: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2', // WETH
      withdrawQueue: SPARK_V2_WITHDRAW_INTENTS,
      yieldKey: sparkVaultV2Key('1', 'spETH'),
    },
    {
      // Seeded but live (~$30k) — real contract, real `vsr`, so it is
      // carried rather than special-cased. Expect it to grow.
      ...sparkV2Base('spPYUSD', 6),
      address: '0x80128dbb9f07b93dde62a6daeadb69ed14a7d354',
      underlying: '0x6c3ea9036406852006290770bedfcaba0e23a0e8', // PYUSD
      withdrawQueue: SPARK_V2_WITHDRAW_INTENTS,
      yieldKey: sparkVaultV2Key('1', 'spPYUSD'),
    },
    {
      // Re Protocol reUSDe — the JUNIOR tranche of the same reinsurance
      // capital stack as reUSD (see `reUsdGroup` for the model, the
      // liquidity caveat, why `isMintable` is false and why the row is
      // therefore `secondaryMarketOnly`). Ethereum only,
      // ~$18.8M, and it earns roughly double reUSD's rate precisely
      // because it absorbs losses first. Underlying is USDe, so unlike
      // reUSD the share and underlying decimals align at 18.
      reader: 'nav-oracle',
      address: '0xddc0f880ff6e4e22e4b74632fbb43ce4df6ccc5a',
      underlying: '0x4c9edd5852cd905f086c759e8383e09bff1e68b3', // USDe
      priceOracle: '0x7844441f13c61cbd3d297f5d2d7fcabf17f0b843',
      mintContract: '0xe1886be2ba8b2496c2044a77516f63a734193082', // ICL
      symbol: 'reUSDe',
      solvency: 'tranched-junior',
      brand: 'Re Protocol',
      description:
        "Junior tranche of Re's reinsurance capital stack, denominated in USDe: it pays roughly double the senior reUSD rate because it absorbs underwriting losses first. Bare ERC-20 priced by a daily NAV oracle. Minting and redeeming at NAV require Re's KYC/AML approval; without it the only way in and out is trading reUSDe on the secondary market (Curve reUSDe/sUSDe, thinner than the reUSD book). For KYC'd holders exits are request-based, with a quarterly queue in the worst case.",
      decimals: 18,
      underlyingDecimals: 18,
      isRebasing: false,
      isMintable: false,
      // Its Curve venue (reUSDe/sUSDe) is materially thinner than reUSD's:
      // quoted -0.64 % at $14k and -2.17 % at $140k. Still the only route
      // without KYC, and the reason the leg refuses to build unbounded.
      secondaryMarketOnly: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 90 * 86_400,
      yieldFetcher: reProtocolFetcher,
      yieldKey: REUSDE_KEY,
    },
    {
      // Frankencoin savings module, Ethereum — the reference deployment
      // and 89 % of the 13.66M ZCHF book. See `frankencoinSavingsBase`.
      //
      // TRAP — the published address config is stale HERE and only here.
      // `@frankencoin/zchf` exports `savingsV2 = 0x3BF301B0…`, which now
      // runs at 1 % and holds 17k ZCHF; the live module is the
      // `savingsReferral` deployment below, at 3.5 % holding 12.13M
      // (re-verified 2026-08-12, `currentRatePPM() = 35000`). Both
      // answer `currentRatePPM()`, so only the balance distinguishes
      // them. The bridged chains have no such split.
      ...frankencoinSavingsBase,
      address: FRANKENCOIN_SAVINGS_MODULES[0].module,
      underlying: ZCHF_ETHEREUM,
      yieldKey: zchfSavingsKey(Chain.ETHEREUM_MAINNET),
    },
    {
      // svZCHF — the TOKENISED wrapper over the same Ethereum module, and the
      // form that is actually composable: it is the collateral of a live
      // LlamaLend market (`oneway-v2-3`, svZCHF against crvUSD), which the
      // bare module can never be because it mints no token.
      //
      // Registered despite holding only ~3.5 ZCHF. Size was the wrong test
      // the first time round: a token being small does not stop it being
      // collateral somewhere, and while it was unregistered there was no way
      // to acquire the one ZCHF form that market accepts.
      //
      // A plain ERC-4626 — `asset()` / `totalAssets()` / `convertToAssets()`
      // all answer, so no bespoke reader. It holds ZERO idle ZCHF and keeps
      // everything in the module, so its share price simply tracks the
      // module's accrual (1.0212 at registration).
      //
      // NOT double-counting the module row beside it: unlike Gnosis, where
      // svZCHF is 62.8 % of that module's book, this wrapper is 3.5 of the
      // Ethereum module's 12.13M — 0.00003 %. The overlap is real but below
      // any rounding a TVL figure survives, and the alternative (dropping one
      // of the two) removes either the yield-bearing token or 12.13M of book.
      //
      // NO `yieldWarmupSeconds`: the 3-day `INTEREST_DELAY` is charged to the
      // VAULT's aggregate position, not to each depositor. A buyer of shares
      // pays the current price and the price keeps growing; what a deposit
      // does is nudge the vault's own clock, diluted across every holder.
      // Publishing a personal 3-day warm-up here would be wrong.
      //
      // `needsDepositApproval` stays default (true): svZCHF is NOT a
      // registered ZCHF minter, so it gets no implicit allowance — verified
      // (`allowance(freshEOA, svZCHF) == 0`), unlike the module itself.
      address: '0xe5f130253ff137f9917c0107659a4c5262abf6b0',
      underlying: ZCHF_ETHEREUM,
      symbol: 'svZCHF',
      brand: 'Frankencoin',
      description:
        "svZCHF is a tokenised wrapper over Frankencoin's ZCHF savings module: it deposits every franc into the module and holds nothing idle, so its share price tracks the governance-set savings rate. Unlike the module itself it is a transferable ERC-20, which is what lets it be used as collateral elsewhere. Deposits and redemptions are instant and permissionless; the module's 3-day interest delay is borne by the vault's aggregate position rather than by each depositor.",
      decimals: 18,
      // A share price that grows — the wrapper's whole point.
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      // The underlying accrual is linear, so the share price grows linearly.
      accrual: 'linear',
      solvency: 'overcollateralized',
      yieldFetcher: frankencoinSavingsFetcher,
      yieldKey: zchfSavingsKey(Chain.ETHEREUM_MAINNET),
    },
    {
      // Curve **scrvUSD** (Savings crvUSD) — an ERC-4626 over crvUSD, and
      // structurally a **Yearn V3 vault that Curve operates**: the share
      // token is an EIP-1167 proxy onto `YearnV3Vault.vy` v3.0.4
      // (`0xd8063123…`, Sourcify-verified), "Multi Strategy". Curve routes
      // crvUSD borrow revenue in and Yearn's profit-unlocking rail releases
      // it over `profitMaxUnlockTime` (7 days) per report.
      //
      // $17.07M held / 15.43M shares, price 1.10649 (2026-08-26). Ethereum is
      // the ONLY vault: the Arbitrum (`0xefb6601d…`), Base (`0x646a737b…`)
      // and XDC (`0x3d8eadb7…`) tokens are bare bridged ERC-20s whose
      // `asset()` and `totalAssets()` revert, and together they hold ~188k
      // shares — 1.2 % of supply. Not registered: there is no vault there.
      // They do share the asset though, and store it under two OTHER group
      // spellings, so `scrvusdFetcher` emits all three and a GROUP_ALIAS
      // folds them into the canonical `Savings crvUSD::scrvUSD`.
      //
      // Instant and free, verified on-chain 2026-08-26: `maxDeposit` is
      // `uint256.max`, `maxRedeem(holder)` equals the holder's balance to
      // the wei (Yearn rounding aside), and `totalDebt` is **0** — every
      // asset sits idle in the vault, so nothing has to be unwound to pay an
      // exit.
      //
      // **Do not take the rate from Curve's `proj_apy`.** It is a projection
      // off `profitUnlockingRate`, which Yearn V3 does not zero when
      // `fullProfitUnlockDate` passes: on 2026-08-26 the vault had been
      // exactly flat for a full day (`convertToAssets` byte-identical,
      // `balanceOf(vault) == 0`) while the field — and Curve's whole API
      // record, `price` and `assets` included — still read 1.301 %.
      // `scrvusdFetcher` measures a 30-day REALIZED delta instead; see its
      // docstring.
      //
      // No `solvency`. Today the shares are backed 1:1 by crvUSD held in the
      // contract (`totalDebt` 0), which would make `overcollateralized`
      // literally true — but this is a Multi-Strategy vault and the
      // allocation is a role-holder dial, not a property of the design. An
      // asserted answer here would be a snapshot of a setting. Absent lets
      // the builder mark `coverage.pending.counterparty` instead, which is
      // the honest statement.
      address: '0x0655977feb2f289a4ab78af67bab0d17aab84367',
      underlying: '0xf939e0a03fb07f59a73314e73794be0e57ac1b4e', // crvUSD
      symbol: 'scrvUSD',
      brand: 'Curve',
      description:
        'crvUSD is Curve\u2019s overcollateralised stablecoin, minted against collateral in its own soft-liquidating CDP markets. scrvUSD is the savings wrapper: Curve routes crvUSD borrow-fee revenue into the vault and it is released into the share price over the week after each deposit, so the price steps up between reports and sits flat in the gaps. Deposits and withdrawals are instant, permissionless, uncapped and free.',
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: scrvusdFetcher,
      yieldKey: SCRVUSD_KEY,
    },
    {
      // Theo **sthUSD** — the ERC-4626 staked wrapper over `thUSD`, Theo's
      // gold-yield stablecoin. Source Sourcify-verified on chain 1
      // (`contracts/thusd/SthUSD.sol` behind the UUPS proxy, impl
      // `0x342487f9…`), so every claim below is read off the code rather
      // than inferred.
      //
      // $24.20M held / 23.83M shares, price 1.015636 (2026-08-25). Ethereum
      // is the ONLY vault: `0xd1db2090…` is a LayerZero OFT Adapter that
      // LOCKS these shares and mints a bare OFT at the same address on
      // Stable (988), Arbitrum, BNB and Mantle — and **97 % of the supply
      // has left for Stable**, where the mirror is the largest Morpho Blue
      // collateral on the chain. See the `'988'` row for that side.
      //
      // **The yield is a PUSH, not a stream, and the row must not imply
      // otherwise.** A `yieldDistributor` calls `setYield(amount)`, which
      // reverts while `getUnvestedAmount() > 0`, so batches cannot overlap;
      // each vests over `vestingDuration` (10,800 s = 3 h) and then nothing
      // accrues until the next push. Measured daily on-chain, the share
      // price steps by at most +142e-6/day (≈ 5.10 % APR) with flat
      // stretches of up to nine days. `sthusdFetcher` reads a 30-day
      // trailing delta for exactly this reason — see its docstring before
      // reaching for a shorter window.
      //
      // **The exit is asynchronous and the wait is not free.**
      // `initiateRedeem(shares, owner)` is permissionless, but it BURNS the
      // shares immediately and snapshots `assets = convertToAssets(shares)`
      // at that block; the claim (`redeem`/`withdraw`) unlocks
      // `lockupPeriod` later and pays that frozen figure, so the five days
      // earn nothing and there is no cancel path. The claim additionally
      // requires `msg.sender == owner_` (`OwnerMustCompleteRedeem`), and
      // `initiateRedeem` books the request under the CALLER — so a
      // third-party redeem via allowance lands the request on the caller,
      // not the holder. Hence `request-based`, never `instant`.
      //
      // TRAP: **`previewRedeem` and `previewWithdraw` are hardcoded
      // `return 0`** — the protocol deprecated them in favour of
      // `maxWithdraw`/`maxRedeem`, which describe only a CLAIMABLE REQUEST
      // and so read 0 for every ordinary holder, including one sitting on
      // 23.2M shares for 27 days. Neither view can size an exit here, and
      // both are the obvious thing to reach for. `previewDeposit` is fine.
      //
      // `erc4626-cooldown` with `cooldownGetter: 'lockupPeriod'`: the pin
      // below is a snapshot of an `onlyOwner` dial with no timelock whose
      // `MAX_LOCKUP_PERIOD` is **30 days** — six times what is configured
      // today. Read it live, like Brix's.
      //
      // `nav-attested`, CURATED — the one field on this row that must not be
      // left to the builder's default. `totalAssets()` is honest about the
      // vault (its own thUSD balance, net of unvested yield and pending
      // redemptions), so the SHARES are backed by the asset; the trust
      // question sits one level down, in what backs thUSD — a gold-leasing
      // and CME-futures-roll book run off-chain and marked by the issuer,
      // with no on-chain solvency invariant anywhere. Absent, the builder
      // falls back to `overcollateralized` and only whispers the doubt
      // through `coverage.pending.counterparty`: a row that says
      // "overcollateralized" about attested gold is wrong in the one field a
      // reader checks before trusting the rate. Naming it also earns the
      // consequences — `nav-drawdown` in `principal.risks`, the
      // `nav-accrual` rate kind — which are all true here: the share price
      // moves only when a distributor chooses to push a batch. Same call as
      // Brix wiTRY.
      reader: 'erc4626-cooldown',
      solvency: 'nav-attested',
      cooldownGetter: 'lockupPeriod',
      address: '0xa808bc9775cb41c52c7842f8b50427fe7a770326',
      underlying: '0xa3fe5c7596024e6811e14f029937d5bd8ae485b3', // thUSD
      symbol: 'sthUSD',
      brand: 'Theo',
      description:
        "thUSD is Theo's gold-yield stablecoin \u2014 the return comes from leasing physical gold and rolling CME futures, not from lending, and the book runs off-chain with no on-chain solvency invariant. sthUSD stakes it with an appreciating share price, but a distributor PUSHES each batch, so the price steps rather than streams and can sit flat for a week. Deposits are permissionless and uncapped. Exiting is a two-step request: initiating burns the shares and fixes the payout at that block's price, and the claim unlocks 5 days later \u2014 the wait earns nothing and cannot be cancelled.",
      decimals: 6,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      // `lockupPeriod()` = 432,000 s on 2026-08-25. Re-read live by the
      // reader; this is only the fallback if that call fails.
      withdrawalCooldownSeconds: 432_000,
      yieldFetcher: sthusdFetcher,
      yieldKey: STHUSD_KEY,
    },
    {
      // Huma **PST** (PayFi Strategy Token), bridged. Huma 2.0 is a
      // SOLANA product: a permissionless USDC pool (no KYC) funding
      // short-dated cross-border payment receivables — originated almost
      // entirely by Arf, a Huma affiliate — whose yield is paid as a rising
      // PST price. 306.6M PST exist ($347M at 1.1325); **148.2M of them are
      // on Ethereum** through Chainlink CCIP, and 125.8M of THOSE sit as
      // collateral in Morpho Blue (84.4M, three listed markets at 86 % LLTV)
      // and Fluid (41.4M, vaults 165/166/169) — the Ethereum book is a
      // loop book, ~22M floats. Assessed + built 2026-09-16; see HUMA.md.
      //
      // This address is NOT a vault. It is Chainlink's `BurnMintERC20`,
      // whose only minter and burner is the CCIP `BurnMintTokenPool 1.6.1`
      // (`0xbe776c85…`, one lane, Solana): no `asset()`, no
      // `convertToAssets()`, no `owner()`, not verified on Sourcify. So there
      // is **no primary mint and no redemption on this chain at all** — the
      // ways in and out are the market (Fluid DEX PST-USDC: a $1M round trip
      // quoted ~4 bps total on KyberSwap at integration) or the bridge back
      // to Solana. Hence `secondaryMarketOnly`: the Re shape, but with an
      // even plainer reason — Re's mint is KYC-gated, this one does not
      // exist. `isMintable: false` states that fact, not a permission.
      //
      // Priced by Chainlink's **"PST-USDC Exchange Rate (Calculated)"**
      // proxy `0x4be50be3…` — the same feed both Morpho PST oracles wrap as
      // `BASE_FEED_1`, so the earn row and the lending rows cannot disagree
      // about what a PST is worth. It answers in **6 decimals** (hence
      // `priceOracleDecimals`), hourly, and — unlike Re's feeds — keeps its
      // FULL round history, which is where `humaPstFetcher` measures the
      // realized rate (7.7 % APR on every window since 2026-05-25). TRAP: the
      // feed's AGGREGATOR reverts `No access` for a contract caller, so any
      // multicall must target the PROXY, which is what this pins.
      //
      // `nav-attested`, asserted: the price is produced by Huma's own
      // off-chain Price Oracle and relayed by Chainlink — an operator-
      // published NAV over an off-chain receivables book with a "modest"
      // first-loss cover and no on-chain solvency invariant (the Theo /
      // Maple / Native trust class). Every consequence the label earns —
      // `nav-accrual` as the rate kind, `nav-drawdown` in the risks — is
      // true here.
      //
      // `withdrawalMode` / cooldown describe the exit a holder who BRIDGES
      // BACK gets on Solana: a redemption request the operator's autotask
      // settles "typically within 1 business day", SLA 7 days, under a global
      // daily cap that rejects further requests once hit. The pin is the SLA
      // worst case (the Re call); a bridge hop each way is on top. For a
      // holder on this chain the exit is the `market` route.
      reader: 'nav-oracle',
      solvency: 'nav-attested',
      address: '0x22ae3d9a738471f405169af055d31c687087d4c7',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      priceOracle: HUMA_PST_FEED,
      priceOracleDecimals: 6,
      symbol: 'PST',
      brand: 'Huma',
      description:
        "Huma's PayFi Strategy Token, bridged from Solana over Chainlink CCIP: a share of Huma 2.0's USDC pool, funding short-dated cross-border payment receivables nearly all originated by Arf, a Huma affiliate; the fee is paid as a rising PST price set by Huma's oracle and relayed by Chainlink. There is no mint and no redemption on Ethereum: you enter and exit by TRADING PST (venue: Fluid DEX PST-USDC) against that NAV, or by bridging back to Solana, where redemption is an operator-settled request under a daily cap. Backing is unsecured receivables credit with a small first-loss cover.",
      decimals: 6,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: false,
      secondaryMarketOnly: true,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 7 * 86_400,
      yieldFetcher: humaPstFetcher,
      yieldKey: HUMA_PST_KEY,
    },
  ],
  '100': [
    {
      // Frankencoin savings module, Gnosis — 1,257,396 ZCHF at
      // 2026-08-12, the second-largest after Ethereum and by far the
      // most active bridged chain (4,853 saves / 13,249 withdrawals).
      // Its own module contract with its own stored rate, kept at the
      // Ethereum 35 000 ppm by a CCIP leadrate push.
      //
      // 62.8 % of this book belongs to the `svZCHF` ERC-4626 wrapper —
      // see the warning on `frankencoinSavingsBase` before adding a row
      // for it.
      ...frankencoinSavingsBase,
      address: FRANKENCOIN_SAVINGS_MODULES[1].module,
      underlying: ZCHF_BRIDGED,
      yieldKey: zchfSavingsKey(Chain.GNOSIS),
    },
  ],
  '42161': [
    {
      // Same CREATE2 family as mainnet spUSDT but an independent vault
      // with its own `vsr`. Seeded (~$1k) at time of writing.
      ...sparkV2Base('spUSDT', 6),
      address: '0x45d91340b3b7b96985a72b5c678f7d9e8d664b62',
      underlying: '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9', // USDT
      yieldKey: sparkVaultV2Key('42161', 'spUSDT'),
    },
  ],
  '196': [
    {
      // X Layer spUSDT — seeded only. Underlying is the bridged USDT
      // (`USDT0`-style) deployment, not mainnet USDT.
      ...sparkV2Base('spUSDT', 6),
      address: '0xc358c90d32375721cb3924320fdc2f8b694347ca',
      underlying: '0x779ded0c9e1022225f8e0630b35a9b54be713736',
      yieldKey: sparkVaultV2Key('196', 'spUSDT'),
    },
  ],
  '8453': [
    {
      address: '0x3a43aec53490cb9fa922847385d82fe25d0e9de7',
      underlying: '0x4200000000000000000000000000000000000006',
      symbol: 'yoETH',
      brand: 'YO',
      description:
        "YO's ETH yield optimizer: WETH is allocated across curated on-chain strategies and yield accrues into the share price. Async ERC-7540 redemption — instant when idle liquidity covers it, up to ~24 hours when unwinding non-liquid strategies.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'request-based',
      yieldFetcher: yoFetcher,
      yieldKey: 'Yield Optimizer ETH::YOETH',
    },
    {
      // Frankencoin savings module, Base — 255,196 ZCHF at 2026-08-12.
      // Note the module address is the SAME on Base and Optimism
      // (`0x6426324a…`, one CREATE2 family); the Optimism twin holds
      // 1 ZCHF and is deliberately unregistered — see
      // `FRANKENCOIN_SAVINGS_MODULES`.
      ...frankencoinSavingsBase,
      address: FRANKENCOIN_SAVINGS_MODULES[2].module,
      underlying: ZCHF_BRIDGED,
      yieldKey: zchfSavingsKey(Chain.BASE),
    },
  ],
  // HyperEVM — the four LIVE core Hyperbeat vaults (Vault-Infra
  // generation, audited by Nethermind + Certora). All facts below were
  // read on-chain / from Hyperbeat's own app config on 2026-08-18:
  //
  //  - The share tokens are NOT ERC-4626 (bare upgradeable ERC-20s, a
  //    renamed Midas mToken lineage) — hence the `hyperbeat-vault` reader
  //    over each vault's Pricer/Depositor/WithdrawalQueue triplet.
  //  - The Midas periphery the DOCS still list is RETIRED: both its
  //    instant paths revert `Pausable: paused` and its NAV aggregators
  //    last updated 2025-12-22. Never build against the docs' addresses.
  //  - Exit is two-legged (`fee-or-queued`), FORK-PROVEN 2026-08-18:
  //    `instantWithdraw` at the queue's `instantWithdrawalFee` (30 bps
  //    live — the docs' 0.5% is the retired stack), paid from the vault's
  //    depositReceiver wallet (`inventoryContract` below — NOT the queue,
  //    whose balance can read 0 while the instant leg still pays); or a
  //    free `createWithdrawalRequest` processed by ops and PAID OUT
  //    AUTOMATICALLY — no claim step. Cancel exists but only fires AFTER
  //    the request's deadline (`WithdrawalRequestDeadlineNotMet` before
  //    it): it is the reclaim path for an expired request, not an abort.
  //  - Deposits are permissionless and uncapped (`isPaused` false,
  //    `depositCap` 0 = uncapped, no fee), via the Depositor
  //    (`mintContract`) — NOT the share token.
  //  - Solvency is deliberately ABSENT: strategies custody assets across
  //    HyperEVM protocols and Hyperliquid (HyperCore) strategist
  //    accounts, and the Pricer's rate is operator-published with no
  //    on-chain solvency invariant (the Native/Maple trust class).
  //
  // Deliberately NOT listed: hbXAUt, wVLP, dnHYPE, dnPUMP — all flagged
  // `windingDown` in Hyperbeat's app config; nLP (Native's wNLP — the
  // `native` provider's surface); hbHYPE/hbBTC (Upshift-era, already
  // served by the LST registry / upshift provider).
  '999': [
    {
      reader: 'hyperbeat-vault',
      address: '0x5e105266db42f78fa814322bce7f388b4c2e61eb',
      underlying: '0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb', // USDT0
      symbol: 'hbUSDT',
      brand: 'Hyperbeat',
      description:
        "hbUSDT deploys USDT0 across HyperEVM protocols and Hyperliquid strategist accounts for market-neutral stablecoin yield (20% performance fee, already netted in the rate). The share price is published by the vault's own Pricer with no on-chain solvency invariant. Exit is two-legged: instant at a 0.3% fee capped by the queue's live inventory, or a free withdrawal request processed within ~1 day and paid out automatically.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      mintContract: '0x6261f30144b259c74243d5f5d9230941186ac936',
      withdrawalMode: 'fee-or-queued',
      withdrawalCooldownSeconds: 86400,
      withdrawQueue: '0x240e0b2cb615ded2fe90fde265b15988dc45b1c6',
      inventoryContract: '0xfd1fd829e4e89cae8190596698e84754c3fec16c',
      priceOracle: '0x3636a26ec1d512c5ecff42f7adaa5ce7964c6579',
      yieldFetcher: hyperbeatVaultsFetcher,
      yieldKey: 'Hyperbeat USDT::hbUSDT',
    },
    {
      reader: 'hyperbeat-vault',
      address: '0x057ced81348d57aad579a672d521d7b4396e8a61',
      underlying: '0xb88339cb7199b77e23db6e890353e22632ba630f', // USDC
      symbol: 'hbUSDC',
      brand: 'Hyperbeat',
      description:
        "hbUSDC deploys USDC across HyperEVM protocols and Hyperliquid strategist accounts for market-neutral stablecoin yield (20% performance fee, already netted in the rate). The share price is published by the vault's own Pricer with no on-chain solvency invariant. Exit is two-legged: instant at a 0.3% fee capped by the queue's live inventory, or a free withdrawal request processed within ~1 day and paid out automatically.",
      decimals: 18,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: true,
      mintContract: '0x929df52b0c3315d03922e12e54b46157395be4d0',
      withdrawalMode: 'fee-or-queued',
      withdrawalCooldownSeconds: 86400,
      withdrawQueue: '0x10024239474120ce410dd7ce203793c81d438be3',
      inventoryContract: '0x7abf6da6c2c131b58c1f4cb3947b0cfe2edc1c2a',
      priceOracle: '0xe0995a641d454c149e6c808baa37cb2b38763316',
      yieldFetcher: hyperbeatVaultsFetcher,
      yieldKey: 'Hyperbeat USDC::hbUSDC',
    },
    {
      reader: 'hyperbeat-vault',
      address: '0x81e064d0eb539de7c3170edf38c1a42cbd752a76',
      underlying: '0x5555555555555555555555555555555555555555', // WHYPE
      symbol: 'lstHYPE',
      brand: 'Hyperbeat',
      description:
        "lstHYPE deploys WHYPE across HyperEVM protocols and Hyperliquid strategist accounts (20% performance fee, already netted in the rate). The rate is HYPE-denominated and the share price is published by the vault's own Pricer with no on-chain solvency invariant. Exit is two-legged: instant at a 0.3% fee capped by the queue's live inventory, or a free withdrawal request processed within ~3 days and paid out automatically.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      mintContract: '0x205ac1e0380b0b9cce691bd4c6f3c14258ae9201',
      withdrawalMode: 'fee-or-queued',
      withdrawalCooldownSeconds: 259200,
      withdrawQueue: '0x8b04cd6561abf2de78112da30ccb919fe8d09d98',
      inventoryContract: '0xa980d98de0fff436e1a3e7d9a06999b03c8aa59e',
      priceOracle: '0x5ed0ec0b0643dab621dc814c8d058e161b9b884b',
      yieldFetcher: hyperbeatVaultsFetcher,
      yieldKey: 'Hyperbeat LST Vault::lstHYPE',
    },
    {
      // Underlying is beHYPE — itself a yield-bearing LST share, so this
      // row's rate is the STRATEGY leg only, in beHYPE terms; beHYPE's
      // staking yield is a separate leg carried by its own assetGroup.
      reader: 'hyperbeat-vault',
      address: '0x441794d6a8f9a3739f5d4e98a728937b33489d29',
      underlying: '0xd8fc8f0b03eba61f64d08b0bef69d80916e5dda9', // beHYPE
      symbol: 'liquidHYPE',
      brand: 'Hyperbeat',
      description:
        "liquidHYPE deploys beHYPE (Hyperbeat x ether.fi staked HYPE) across HyperEVM strategies; the rate shown is the strategy leg in beHYPE terms and comes ON TOP of beHYPE's own staking yield. The share price is published by the vault's own Pricer with no on-chain solvency invariant. Exit is two-legged: instant at a 0.3% fee capped by the queue's live inventory, or a free withdrawal request processed within ~3 days and paid out automatically.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      mintContract: '0x5bfb09dd155c0ec3f375b266a7353c0ba64f9d60',
      withdrawalMode: 'fee-or-queued',
      withdrawalCooldownSeconds: 259200,
      withdrawQueue: '0xa03e0e3b7e6204c9a8d237c4fbd30793555a84fe',
      inventoryContract: '0xb3f15e41fc1536e47ee7de20c7b44fb1eec70aec',
      priceOracle: '0x90a0a650f0c403a92ae22f162b3e61818d6f8f11',
      yieldFetcher: hyperbeatVaultsFetcher,
      yieldKey: 'Liquid HYPE Yield::liquidHYPE',
    },
  ],
  '43114': [
    {
      address: '0x06d47f3fb376649c3a9dafe069b3d6e35572219e',
      underlying: '0x24de8771bc5ddb3362db529fc3358f2df3a0e346',
      symbol: 'savUSD',
      brand: 'Avant',
      description:
        "avUSD is Avant's synthetic dollar; savUSD stakes it and accrues yield from Avant's market-neutral trading strategies via an appreciating share price, with a 24-hour unstake cooldown.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 86_400,
      yieldFetcher: savusdFetcher,
      yieldKey: 'Staked avUSD::savUSD',
    },
    {
      // Avant savBTC — staked avBTC, StakedUSDeV2-style 24h cooldown.
      address: '0x649342c6bff544d82df1b2ba3c93e0c22cdeba84',
      underlying: '0xfd2c2a98009d0cbed715882036e43d26c4289053', // avBTC
      symbol: 'savBTC',
      brand: 'Avant',
      description:
        "avBTC is Avant's BTC-denominated synthetic asset; savBTC stakes it and accrues yield from Avant's market-neutral strategies via an appreciating share price, with a 24-hour unstake cooldown.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 86_400,
      yieldFetcher: savbtcFetcher,
      yieldKey: 'Staked avBTC::savBTC',
    },
    {
      // Spark Vaults V2 spUSDC on Avalanche — same CREATE2 address as
      // the mainnet vault, independent deployment and `vsr`.
      ...sparkV2Base('spUSDC', 6),
      address: '0x28b3a8fb53b741a8fd78c0fb9a6b2393d896a43d',
      underlying: '0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e', // USDC
      yieldKey: sparkVaultV2Key('43114', 'spUSDC'),
    },
  ],
  // ---------------------------------------------------------------------
  // Stable (988) — a Tether-adjacent L1 whose whole lending surface is one
  // Morpho Blue deployment (`0xa40103…`), and whose largest market by an
  // order of magnitude is sthUSD/USDT0: $23.03M of collateral against
  // $18.59M supplied / $16.81M borrowed at 86 % LLTV. The earn row and that
  // collateral leg are the same asset, so registering it here is also what
  // gives the pair book a non-zero carry on the chain's biggest position.
  // ---------------------------------------------------------------------
  '988': [
    {
      // Theo **sthUSD**, bridged. This address is NOT the vault — it is the
      // LayerZero OFT minted against shares locked in the Ethereum OFT
      // Adapter at the same address, and it is a bare ERC-20 with a
      // blacklist and a pause: `asset()`, `totalAssets()`, `convertToAssets()`
      // and every `max*` view revert. See the chain-1 row for the vault,
      // its push-yield mechanism and its two-step exit.
      //
      // Priced by RedStone's **"RedStone Price Feed for sthUSD_FUNDAMENTAL"**
      // `0xb81131b6…` — the same feed Morpho's own oracle for this market
      // reads (`BASE_FEED_1` under a 1e28 `SCALE_FACTOR`), so the earn row
      // and the lending row cannot disagree about what a share is worth. It
      // is Chainlink-shaped and fresh, but answers in **8 decimals** (hence
      // `priceOracleDecimals`) and pins `roundId` at 1, i.e. NO round
      // history — an on-chain rate delta is impossible here, which is one
      // more reason `sthusdFetcher` measures the Ethereum vault instead.
      //
      // `underlying` is USDT0, the chain's dollar and this market's loan
      // asset: thUSD itself was never bridged to Stable, so there is no
      // local asset to name. Same call as Re's rows, which point at USDC.
      //
      // `isMintable: false` states a plain fact rather than a permission:
      // there is no mint function on an OFT and no vault on this chain, so
      // the only way in is to bridge from Ethereum. And it is deliberately
      // NOT `secondaryMarketOnly` — that flag promises a tradeable market,
      // and there isn't one: **23,216,009 of the 23,216,028 supply
      // (99.99997 %) sits inside Morpho Blue as collateral**, leaving ~19
      // sthUSD of free float across the whole chain. A `via: 'swap'` route
      // would advertise depth that does not exist.
      reader: 'nav-oracle',
      // Same curated answer as the chain-1 row — one asset cannot have two
      // trust stories. Here it is doubly right: this side's price IS an
      // attestation, published by RedStone rather than derived from anything
      // the chain holds.
      solvency: 'nav-attested',
      address: '0xd1db209087516883ec705cfeb99e80bb6032d540',
      underlying: '0x779ded0c9e1022225f8e0630b35a9b54be713736', // USDT0
      priceOracle: '0xb81131b6368b3f0a83af09db4e39ac23da96c2db',
      priceOracleDecimals: 8,
      symbol: 'sthUSD',
      brand: 'Theo',
      description:
        "Bridged sthUSD — Theo's staked gold-yield dollar, whose vault lives on Ethereum; this is the LayerZero token minted against shares locked there, priced by RedStone's sthUSD NAV feed. It holds 97 % of the supply and is the largest Morpho Blue collateral on Stable. There is no vault on this chain: entering and exiting both mean bridging to Ethereum, where redemption is a request that burns the shares at today's price and pays out 5 days later.",
      decimals: 6,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: false,
      withdrawalMode: 'request-based',
      // The Ethereum vault's `lockupPeriod`, plus a bridge hop each way that
      // this number does not include.
      withdrawalCooldownSeconds: 432_000,
      yieldFetcher: sthusdFetcher,
      yieldKey: STHUSD_KEY,
    },
  ],
  // ---------------------------------------------------------------------
  // Arc (5042) — Circle's USDC-gas L1, mainnet 2026-09-16. One row: Huma's
  // PST, bridged in over Chainlink CCIP the same day.
  // ---------------------------------------------------------------------
  '5042': [
    {
      // Huma **PST**, bridged to Arc. Same asset and same trust story as the
      // chain-1 row (see it for the product); what differs is the SHAPE of
      // the exit. On Ethereum PST has $16M of DEX depth, so the row is
      // `secondaryMarketOnly`. Here it has NONE: KyberSwap answers
      // `route not found` for PST on Arc while routing USDC→WETH fine, and
      // at integration the whole bridged supply (441,548 PST, ≈ $500k) sat
      // inside the one listed Morpho Blue PST/USDC market — the Theo-on-
      // Stable shape, where a `via: 'swap'` route would advertise depth that
      // does not exist. So `isMintable: false` (a CCIP `BurnMintERC20` has
      // no mint function) and NOT `secondaryMarketOnly`: the only way in or
      // out is the bridge back to Solana. Flip the flag when a venue appears.
      //
      // Priced by Chainlink's **"PSTUSDC / USDC Exchange Rate"** proxy
      // `0x1abf15e8…` on Arc — the feed the Morpho market's oracle wraps as
      // `BASE_FEED_1`, so the earn row and the lending row agree. 6 decimals
      // TODAY; TRAP: its round history is NOT uniformly scaled — rounds 1
      // to ~100 (2026-06-19 → 06-23) answered at 12 decimals
      // (`1111266641610`) before the feed settled at 6 — so never derive a
      // rate from this feed's history. The rate is `humaPstFetcher`, which
      // measures the Ethereum feed; PST accrues identically on every chain
      // (1.132518 here vs 1.132511 on Ethereum at the same hour).
      reader: 'nav-oracle',
      solvency: 'nav-attested',
      address: '0xa6db07ebf438d91aa653746fc05523e435645525',
      underlying: '0x3600000000000000000000000000000000000000', // USDC (ERC-20)
      priceOracle: '0x1abf15e82566d274bf8722ac3e42fc3c79304051',
      priceOracleDecimals: 6,
      symbol: 'PST',
      brand: 'Huma',
      description:
        "Huma's PayFi Strategy Token, bridged to Arc over Chainlink CCIP: a share of Huma 2.0's USDC pool on Solana, funding short-dated cross-border payment receivables nearly all originated by Arf, a Huma affiliate; the fee is paid as a rising PST price set by Huma's oracle and relayed by Chainlink. There is no mint, no redemption and no trading venue on Arc: the only way in or out is bridging back to Solana, where redemption is an operator-settled request under a daily cap. Backing is unsecured receivables credit with a small first-loss cover.",
      decimals: 6,
      underlyingDecimals: 6,
      isRebasing: false,
      isMintable: false,
      withdrawalMode: 'request-based',
      withdrawalCooldownSeconds: 7 * 86_400,
      yieldFetcher: humaPstFetcher,
      yieldKey: HUMA_PST_KEY,
    },
  ],
  '4663': [
    {
      // Spark Vaults V2 spUSDG on Robinhood Chain — the first V2 vault
      // we carried. Its APR now comes from `vsr()` like every other V2
      // vault (the old chi-delta fetcher assumed no readable rate; the
      // getter is `vsr`, not `ssr`), and its exit is idle-capped like
      // the rest of the family rather than unconditionally instant.
      ...sparkV2Base('spUSDG', 6),
      address: '0xde770c84fe66e063336b31737cfe9790f18c4087',
      underlying: '0x5fc5360d0400a0fd4f2af552add042d716f1d168', // USDG
      yieldKey: SPARK_SAVINGS_USDG_KEY,
    },
  ],
}

/**
 * Per-chain savings-vault allowlist. Assembled at module load from
 * single-chain entries, the expanded `MULTI_CHAIN_GROUPS`, and the
 * Native Credit Pool wNLP table in
 * [registryNative.ts](./registryNative.ts).
 *
 * See [TIMELOCKS_AND_CHAINS.md](./TIMELOCKS_AND_CHAINS.md) for the
 * verification notes and
 * [../lst/EXPANSION_PLAN.md](../lst/EXPANSION_PLAN.md) for the
 * rollout order.
 */
export const SAVINGS_REGISTRY: { [chainId: string]: SavingsRegistryEntry[] } =
  (() => {
    const out: { [chainId: string]: SavingsRegistryEntry[] } = {}
    for (const [chainId, entries] of Object.entries(SINGLE_CHAIN_ENTRIES)) {
      out[chainId] = [...entries]
    }
    for (const group of MULTI_CHAIN_GROUPS) {
      for (const [chainId, addrs] of Object.entries(group.chains)) {
        if (!out[chainId]) out[chainId] = []
        out[chainId].push({ ...group.base, ...addrs })
      }
    }
    for (const [chainId, entries] of Object.entries(NATIVE_WNLP_ENTRIES)) {
      if (!out[chainId]) out[chainId] = []
      out[chainId].push(...entries)
    }
    for (const [chainId, entries] of Object.entries(BITWAY_ENTRIES)) {
      if (!out[chainId]) out[chainId] = []
      out[chainId].push(...entries)
    }
    for (const [chainId, entries] of Object.entries(BITFI_ENTRIES)) {
      if (!out[chainId]) out[chainId] = []
      out[chainId].push(...entries)
    }
    for (const [chainId, entries] of Object.entries(VESPER_ENTRIES)) {
      if (!out[chainId]) out[chainId] = []
      out[chainId].push(...entries)
    }
    for (const [chainId, entries] of Object.entries(VENUS_HUB_ENTRIES)) {
      if (!out[chainId]) out[chainId] = []
      out[chainId].push(...entries)
    }
    return out
  })()

/** Returns the registry entries for a chain, or `[]` when unsupported. */
/**
 * How a user's position in `address` is read, from OUR registry.
 *
 * Deliberately a local lookup rather than a field on whatever payload built
 * the vault entry. Whether an address is an ERC-20 is a static protocol fact
 * we already know; sourcing it from the recorder origin — which is what
 * `/v1/data/vaults/user` does for the rest of a lookup entry — means the
 * answer silently becomes `undefined` for every origin-backed request, the
 * balance read falls back to `balanceOf`, and a Frankencoin savings position
 * reads as zero because `balanceOf` reverts on the module.
 *
 * Returns `undefined` for anything unregistered, which callers must treat as
 * the ordinary `balanceOf` shape.
 */
export const savingsBalanceKind = (
  chainId: string,
  address: string,
): 'erc20' | 'savings-account' | undefined => {
  const lc = address.toLowerCase()
  return (SAVINGS_REGISTRY[chainId] ?? []).find((e) => e.address === lc)
    ?.balanceKind
}

/**
 * Is this row one whose only route in and out — for a caller without the
 * protocol's KYC/allowlist — is a trade on the secondary market?
 *
 * The one source of truth for that question. `capabilities.ts` reads it to
 * publish `via: 'swap'` on both legs, and worker-api's vault routes read it to
 * build the trade instead of 4626 calldata the token does not implement.
 * Deriving it twice is how the advertisement and the route drift apart.
 */
export const isSecondaryMarketOnly = (
  chainId: string,
  address: string,
): boolean =>
  (SAVINGS_REGISTRY[chainId] ?? []).some(
    (e) =>
      e.address === address.toLowerCase() && e.secondaryMarketOnly === true,
  )

/** What a secondary-market row trades as: the pair's default other side (its
 *  registered underlying, i.e. what a mint would have taken) and the symbol,
 *  so a refusal can name the token instead of a category. */
export const secondaryMarketVault = (
  chainId: string,
  address: string,
): { underlying: string; symbol: string } | undefined => {
  const e = (SAVINGS_REGISTRY[chainId] ?? []).find(
    (x) =>
      x.address === address.toLowerCase() && x.secondaryMarketOnly === true,
  )
  return e ? { underlying: e.underlying, symbol: e.symbol } : undefined
}

/** Every savings vault we register on a chain, addresses lowercased. */
export const savingsAddresses = (chainId: string): string[] =>
  (SAVINGS_REGISTRY[chainId] ?? []).map((e) => e.address)

export const getSavingsRegistry = (chainId: string): SavingsRegistryEntry[] =>
  SAVINGS_REGISTRY[chainId] ?? []
